import {
	catalog,
	categories,
	categoryBySlug,
	groups,
} from "@sobidogam/catalog";
import {
	activeCategoryLimits,
	applySensitivityPolicy,
	calculateCategoryLimits,
	calculateDailyAllowance,
	calculateLivingBudget,
	classify,
	classifyWithRules,
	type DayClose as DayCloseRecord,
	dateAnchor,
	decideArchiveOffer,
	deriveBackupKey,
	deriveCategoryVisits,
	deriveDayDogam,
	deriveDayKind,
	emptyHouseholdConsumption,
	findComparableMemories,
	findDuplicate,
	findReflectionCandidates,
	forecastRestock,
	formatKstDate,
	formatKstDateTime,
	generateRecoveryCode,
	type HouseholdConsumptionState,
	hasLivingBudget,
	inPeriod,
	isHomeItemCategory,
	kstDateKey,
	type MemoryMood,
	type MemoryOccasion,
	normalizeRecoveryCode,
	normalizeShoppingEntryIds,
	normalizeStoreName,
	type PaymentTraceSource,
	type PeriodKind,
	type PurchaseReason,
	type PurchaseSatisfaction,
	parseDayCloses,
	parsePaymentNotificationText,
	queueRestock,
	type RepurchaseIntent,
	reconcileReceiptItems,
	recurringPaymentRecorded,
	removeStoreRule,
	resolveLivingBudgetPlan,
	resolvePeriod,
	restoreReceiptItems,
	reverseReceiptItems,
	setStoreRule,
	undoSavedReceiptStock,
	unlockStage,
	upsertDayClose,
} from "@sobidogam/domain";
import {
	type Channel,
	type CommerceEntitlement,
	type CommercePort,
	createAnalyticsPort,
	createInstallIdentity,
	type IdentityPort,
	identityHeaders,
} from "@sobidogam/platform-ports";
import type { ReportPeriod } from "@sobidogam/report";
import {
	buildAnnualReportPdf,
	buildMemoryArchive,
	type CsvPaymentDraft,
	canvasBlob,
	exportCsv,
	importPaymentCsv,
	renderMonthDayCard,
	renderUnlockCard,
	shareCardBlob,
	summarizeReports,
} from "@sobidogam/report";
import {
	createReceiptStore,
	createSerialAsyncQueue,
	deleteLocalDataQuarantine,
	isLocalSnapshot,
	LocalBackupRollbackError,
	type LocalDataLoadResult,
	type LocalDataQuarantine,
	listDayCloseRecoveryCopies,
	listLocalDataQuarantines,
	loadDayCloses,
	loadLocalData,
	normalizeReceiptForStorage,
	persistReceiptAndHouseholdChange,
	quarantineLocalData,
	type Receipt,
	ReceiptHouseholdPersistError,
	restoreLocalBackup,
	writeDayCloses,
	writeLocalHousehold,
} from "@sobidogam/storage";
import {
	type ChangeEvent,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react";
import { CalendarView } from "./CalendarView";
import {
	icsBlob,
	monthCloseReminder,
	recurringReminder,
	weeklyReminder,
} from "./calendar-reminders";
import { Header, navigate, SiteFooter } from "./chrome";
import { DayClose } from "./DayClose";
import {
	type DayCloseRecovery,
	DayCloseRecoveryNotice,
	dayCloseRecoveryBlocksPath,
} from "./DayCloseRecovery";
import { DayDogam } from "./DayDogam";
import { GenericCsvImport } from "./GenericCsvImport";
import { HelpSearch } from "./HelpSearch";
import { HouseholdPage } from "./HouseholdPage";
import { Icon } from "./Icons";
import { InstallGuide, shouldOfferInstall } from "./InstallPrompt";
import { IssueReportButton } from "./IssueReport";
import {
	installJuiceUnlock,
	isJuiceMuted,
	playJuice,
	setJuiceMuted,
} from "./juice";
import { KoreanDateTimeField } from "./KoreanDateTimeField";
import { Landing } from "./Landing";
import { LegalPage } from "./Legal";
import { PlusScreen } from "./PlusScreen";
import { monthStartDayPref, readStoreRules, writeStoreRules } from "./prefs";
import { guideBySlug } from "./public/guides";
import {
	CatalogIndex,
	CategoryDetail,
	GuidePage,
	PricingPage,
} from "./public/PublicPages";
import {
	PURCHASE_REASON_LABELS,
	REPURCHASE_LABELS,
	SATISFACTION_LABELS,
} from "./purchase-labels";
import { QuickRecord } from "./QuickRecord";
import { ReceiptItemsEditor } from "./ReceiptItemsEditor";
import { type RecordEdit, RecordEditor } from "./RecordEditor";
import { RecordingRoutes } from "./RecordingRoutes";
import {
	type PeriodChoice,
	PeriodFilter,
	resolvePeriodChoice,
	StoreSummary,
	TagManager,
} from "./RecordTools";
import {
	AnnualPreview,
	CategoryChart,
	MonthCloseCard,
	OnThisDay,
	StatementCheck,
	statementMatched,
	TagTotals,
} from "./Reflection";
import { rememberError } from "./recent-error";
import { SaveToast, type SaveToastState } from "./SaveToast";
import { Sheet } from "./Sheet";
import { type CardShareOutcome, shareCard, shareText } from "./share";
import {
	daysSinceFirstBucket,
	firstRecordSignal,
	firstWeekSummary,
	markCaptureStarted,
	useServerFeatures,
} from "./signals";
import { AllowanceEditor, TodayScreen } from "./TodayScreen";
import { readTags, type TagDef, tagLabel, writeTags } from "./tags";
import { WeeklyCloseSummary } from "./WeeklyCloseSummary";
import "./daily-use.css";
import "./complete.css";
import "./next6.css";

export interface ProductUiProps {
	channel: Channel;
	appName?: string;
	apiBaseUrl?: string;
	commerce?: CommercePort | undefined;
	identity?: IdentityPort | undefined;
	webPushPublicKey?: string | undefined;
	requestPushPermission?: (() => Promise<boolean>) | undefined;
	/** Channel bridge (Apps in Toss clipboard/share). Web falls back to browser APIs. */
	bridge?: ChannelBridge | undefined;
}

export interface ChannelBridge {
	readClipboard?(): Promise<string | null>;
	shareText?(text: string, path?: string): Promise<"shared" | "failed">;
	haptic?(): void;
}

const MOOD_LABELS: Record<MemoryMood, string> = {
	joy: "설렘",
	comfort: "편안함",
	curious: "새로움",
	ordinary: "평범한 날",
};

const OCCASION_LABELS: Record<MemoryOccasion, string> = {
	solo: "혼자",
	together: "함께",
	gift: "선물",
	work: "일하며",
	routine: "일상 루틴",
};

function base64Url(bytes: Uint8Array): string {
	return btoa(String.fromCharCode(...bytes))
		.replaceAll("+", "-")
		.replaceAll("/", "_")
		.replace(/=+$/g, "");
}

function fromBase64Url(value: string): Uint8Array {
	const padded = value.replaceAll("-", "+").replaceAll("_", "/");
	const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
	return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

async function checksumReceipts(receipts: Receipt[]): Promise<string> {
	const digest = await crypto.subtle.digest(
		"SHA-256",
		new TextEncoder().encode(JSON.stringify(receipts)),
	);
	return base64Url(new Uint8Array(digest));
}

async function recoveryCodeHash(code: string): Promise<string> {
	const digest = new Uint8Array(
		await crypto.subtle.digest(
			"SHA-256",
			new TextEncoder().encode(normalizeRecoveryCode(code)),
		),
	);
	return [...digest].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function downloadBlob(blob: Blob, filename: string) {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
}

export async function encryptBackup(
	receipts: Receipt[],
	household: HouseholdConsumptionState,
	dayCloses: DayCloseRecord[],
) {
	const entropy = crypto.getRandomValues(new Uint8Array(24));
	const salt = crypto.getRandomValues(new Uint8Array(16));
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const code = generateRecoveryCode(entropy);
	const key = await deriveBackupKey(code, salt);
	const checksum = await checksumReceipts(receipts);
	const payloadValue = {
		schemaVer: 2,
		exportedAt: new Date().toISOString(),
		receipts,
		household,
		dayCloses,
		checksum,
	};
	const payload = new TextEncoder().encode(JSON.stringify(payloadValue));
	const encrypted = new Uint8Array(
		await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, payload),
	);
	const combined = new Uint8Array(iv.length + encrypted.length);
	combined.set(iv);
	combined.set(encrypted, iv.length);
	const verification = await crypto.subtle.decrypt(
		{ name: "AES-GCM", iv },
		key,
		encrypted,
	);
	const verified = JSON.parse(new TextDecoder().decode(verification)) as {
		receipts?: Receipt[];
		household?: HouseholdConsumptionState;
		checksum?: string;
	};
	if (
		!Array.isArray(verified.receipts) ||
		verified.receipts.length !== receipts.length ||
		verified.checksum !== checksum ||
		verified.household?.schemaVersion !== 1
	)
		throw new Error("E-CODE-01");
	return {
		code,
		envelope: {
			schemaVer: 2,
			saltB64: base64Url(salt),
			ciphertextB64: base64Url(combined),
			createdAt: new Date().toISOString(),
		},
	};
}

export async function decryptBackup(
	file: File,
	code: string,
): Promise<{
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	dayCloses: DayCloseRecord[];
}> {
	const raw: unknown = JSON.parse(await file.text());
	if (
		Array.isArray(raw) ||
		(raw && typeof raw === "object" && "receipts" in raw)
	) {
		const plain = Array.isArray(raw)
			? {
					receipts: raw,
					household: emptyHouseholdConsumption(),
					dayCloses: undefined,
				}
			: (raw as {
					receipts: unknown;
					household?: unknown;
					dayCloses?: unknown;
				});
		const household = plain.household ?? emptyHouseholdConsumption();
		if (!isLocalSnapshot(plain.receipts, household))
			throw new Error("E-CODE-01");
		return {
			receipts: plain.receipts as Receipt[],
			household: household as HouseholdConsumptionState,
			dayCloses: parseDayCloses(
				plain.dayCloses === undefined ? null : JSON.stringify(plain.dayCloses),
			),
		};
	}
	const envelope = raw as {
		schemaVer?: number;
		saltB64?: string;
		ciphertextB64?: string;
	};
	if (
		(envelope.schemaVer !== 1 && envelope.schemaVer !== 2) ||
		typeof envelope.saltB64 !== "string" ||
		typeof envelope.ciphertextB64 !== "string"
	)
		throw new Error("E-CODE-01");
	const normalized = normalizeRecoveryCode(code);
	const combined = fromBase64Url(envelope.ciphertextB64);
	const iv = combined.slice(0, 12);
	const ciphertext = combined.slice(12);
	const key = await deriveBackupKey(
		normalized,
		fromBase64Url(envelope.saltB64),
	);
	const plain = await crypto.subtle.decrypt(
		{ name: "AES-GCM", iv },
		key,
		ciphertext,
	);
	const payload = JSON.parse(new TextDecoder().decode(plain)) as {
		receipts?: Receipt[];
		household?: HouseholdConsumptionState;
		dayCloses?: unknown;
		checksum?: string;
	};
	if (!Array.isArray(payload.receipts)) throw new Error("E-CODE-01");
	if (
		!isLocalSnapshot(
			payload.receipts,
			payload.household ?? emptyHouseholdConsumption(),
		)
	)
		throw new Error("E-CODE-01");
	if (
		payload.checksum &&
		(await checksumReceipts(payload.receipts)) !== payload.checksum
	)
		throw new Error("E-CODE-01");
	return {
		receipts: payload.receipts,
		dayCloses: parseDayCloses(
			payload.dayCloses === undefined
				? null
				: JSON.stringify(payload.dayCloses),
		),
		household:
			payload.household?.schemaVersion === 1
				? payload.household
				: emptyHouseholdConsumption(),
	};
}

let analytics = createAnalyticsPort();
/** Set by entry points (share target, shortcut deep link) before confirm. */
let pendingCaptureMode: string | null = null;
export function setPendingCaptureMode(mode: string | null) {
	pendingCaptureMode = mode;
}

function writeHousehold(state: HouseholdConsumptionState) {
	writeLocalHousehold(window.localStorage, state);
}

function mergeHouseholdCollection<T extends object>(
	before: T[],
	current: T[],
	intended: T[],
	keyOf: (value: T) => string,
): T[] {
	const beforeByKey = new Map(before.map((value) => [keyOf(value), value]));
	const intendedByKey = new Map(intended.map((value) => [keyOf(value), value]));
	const merged = current.flatMap((currentValue) => {
		const key = keyOf(currentValue);
		const baseline = beforeByKey.get(key);
		const next = intendedByKey.get(key);
		if (!next && baseline) return [];
		if (!next || !baseline || JSON.stringify(next) === JSON.stringify(baseline))
			return [currentValue];
		const patch = Object.fromEntries(
			Object.entries(next as Record<string, unknown>).filter(
				([field, value]) =>
					JSON.stringify(value) !==
					JSON.stringify((baseline as Record<string, unknown>)[field]),
			),
		);
		return [{ ...currentValue, ...patch } as T];
	});
	for (const next of intended) {
		const key = keyOf(next);
		if (!beforeByKey.has(key) && !merged.some((value) => keyOf(value) === key))
			merged.push(next);
	}
	return merged;
}

/** Applies a household UI edit without losing receipt-linked changes ACKed first. */
function mergeHouseholdEdit(
	before: HouseholdConsumptionState,
	current: HouseholdConsumptionState,
	intended: HouseholdConsumptionState,
): HouseholdConsumptionState {
	return {
		...current,
		items: mergeHouseholdCollection(
			before.items,
			current.items,
			intended.items,
			(item) => item.itemId,
		),
		shopping: mergeHouseholdCollection(
			before.shopping,
			current.shopping,
			intended.shopping,
			(entry) => entry.entryId,
		),
		budgetPlans: mergeHouseholdCollection(
			before.budgetPlans,
			current.budgetPlans,
			intended.budgetPlans,
			(plan) => plan.period,
		),
		recurringPayments: mergeHouseholdCollection(
			before.recurringPayments ?? [],
			current.recurringPayments ?? [],
			intended.recurringPayments ?? [],
			(payment) => payment.id,
		),
	};
}

type LocalRecoveryState = Extract<LocalDataLoadResult, { status: "recovery" }>;

const LOCAL_ISSUE_LABELS = {
	receipts: "소비 기록",
	household: "집 안 생활 기록",
} as const;

const LOCAL_REASON_LABELS = {
	read_error: "브라우저 저장소에서 읽지 못했어요.",
	invalid_json: "저장 형식이 중간에 끊겼어요.",
	invalid_schema: "현재 앱이 안전하게 읽을 수 없는 형식이에요.",
} as const;

function downloadLocalRaw(raw: string, filename: string) {
	downloadBlob(new Blob([raw], { type: "text/plain;charset=utf-8" }), filename);
}

function safeListLocalDataQuarantines(): LocalDataQuarantine[] {
	try {
		return listLocalDataQuarantines(window.localStorage);
	} catch {
		return [];
	}
}

function QuarantineDownloads({ copy }: { copy: LocalDataQuarantine }) {
	return (
		<div className="recovery-copy">
			<p>
				<strong>{new Date(copy.createdAt).toLocaleString("ko-KR")}</strong>
				<span>원문 두 묶음을 그대로 보관했어요.</span>
			</p>
			<div className="recovery-actions">
				{copy.sources.receipts.raw !== null && (
					<button
						className="quiet-button"
						type="button"
						onClick={() =>
							downloadLocalRaw(
								copy.sources.receipts.raw ?? "",
								`sobidogam-receipts-${copy.id}.txt`,
							)
						}
					>
						소비 기록 원문 받기
					</button>
				)}
				{copy.sources.household.raw !== null && (
					<button
						className="quiet-button"
						type="button"
						onClick={() =>
							downloadLocalRaw(
								copy.sources.household.raw ?? "",
								`sobidogam-household-${copy.id}.txt`,
							)
						}
					>
						생활 기록 원문 받기
					</button>
				)}
			</div>
		</div>
	);
}

function LocalRecoveryScreen({
	result,
	onRetry,
	onRecovered,
}: {
	result: LocalRecoveryState;
	onRetry: () => void;
	onRecovered: () => void;
}) {
	const [confirmReset, setConfirmReset] = useState(false);
	const [error, setError] = useState("");
	const recoveryTitleId = useId();
	const previousCopies = safeListLocalDataQuarantines();
	const reset = () => {
		setError("");
		try {
			quarantineLocalData(window.localStorage, result);
			onRecovered();
		} catch {
			setConfirmReset(false);
			setError(
				"원문을 안전하게 보관하지 못해 비우지 않았어요. 저장 공간을 확인한 뒤 다시 시도해 주세요.",
			);
			onRetry();
		}
	};
	return (
		<main className="local-recovery-page">
			<section
				className="local-recovery-card"
				aria-labelledby={recoveryTitleId}
			>
				<p className="eyebrow">기록 복구</p>
				<h1 id={recoveryTitleId}>저장한 기록을 안전하게 열지 못했어요.</h1>
				<p className="page-lede">
					원문이 빈 기록으로 덮이지 않도록 모든 저장과 정상 화면을 멈췄어요.
				</p>
				<ul className="recovery-issues">
					{result.issues.map((issue) => (
						<li key={`${issue.source}-${issue.reason}`}>
							<strong>{LOCAL_ISSUE_LABELS[issue.source]}</strong>
							<span>{LOCAL_REASON_LABELS[issue.reason]}</span>
						</li>
					))}
				</ul>
				<div className="recovery-actions">
					{result.raw.receipts !== null && (
						<button
							className="quiet-button"
							type="button"
							onClick={() =>
								downloadLocalRaw(
									result.raw.receipts ?? "",
									"sobidogam-receipts-original.txt",
								)
							}
						>
							소비 기록 원문 먼저 받기
						</button>
					)}
					{result.raw.household !== null && (
						<button
							className="quiet-button"
							type="button"
							onClick={() =>
								downloadLocalRaw(
									result.raw.household ?? "",
									"sobidogam-household-original.txt",
								)
							}
						>
							생활 기록 원문 먼저 받기
						</button>
					)}
				</div>
				{error && (
					<output className="notice notice-danger" role="alert">
						{error}
					</output>
				)}
				<div className="recovery-primary-actions">
					<button className="quiet-button" type="button" onClick={onRetry}>
						다시 읽기
					</button>
					{result.canQuarantine && !confirmReset && (
						<button
							className="button"
							type="button"
							onClick={() => setConfirmReset(true)}
						>
							원문을 보관하고 새로 시작
						</button>
					)}
				</div>
				{result.canQuarantine && confirmReset && (
					<fieldset className="recovery-confirm">
						<legend className="sr-only">초기화 확인</legend>
						<p>
							두 원문을 복구 보관함에 먼저 복사하고 확인한 뒤 현재 저장본을
							비웁니다. 기기 저장본이 있으면 그다음에 다시 불러와요.
						</p>
						<div className="recovery-actions">
							<button
								className="quiet-button"
								type="button"
								onClick={() => setConfirmReset(false)}
							>
								취소
							</button>
							<button className="button" type="button" onClick={reset}>
								원문 격리 후 비우기
							</button>
						</div>
					</fieldset>
				)}
				{previousCopies.length > 0 && (
					<div className="previous-recovery-copies">
						<h2>이전에 보관한 원문</h2>
						{previousCopies.map((copy) => (
							<QuarantineDownloads key={copy.storageKey} copy={copy} />
						))}
					</div>
				)}
			</section>
		</main>
	);
}

async function installHeaders(
	channel: Channel,
	identity?: IdentityPort,
): Promise<Record<string, string>> {
	return identityHeaders(
		identity ?? createInstallIdentity(window.localStorage, channel),
		"0.3.0",
	);
}

function formatWon(value: number) {
	return `${value.toLocaleString("ko-KR")}원`;
}

function sourceLabel(source: PaymentTraceSource) {
	return {
		ai: "영수증 자동 읽기",
		manual: "직접 입력",
		notification: "결제 알림",
		screenshot: "이미지·문서",
		csv: "가계부 기록",
	}[source];
}

function toKstDateTimeInput(iso: string) {
	const parsed = new Date(iso);
	return Number.isFinite(parsed.getTime())
		? new Date(parsed.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 16)
		: "";
}

function SymbolMark({
	symbolId,
	locked = false,
}: {
	symbolId: string;
	locked?: boolean;
}) {
	const glyph: Record<string, string> = {
		cup: "◌",
		bread: "⌁",
		bowl: "◡",
		meat: "◆",
		fish: "◈",
		bottle: "♢",
		cart: "□",
		store: "▣",
		bag: "▱",
		shirt: "◇",
		shoe: "⌒",
		gem: "✦",
		sparkle: "✧",
		pill: "○",
		stethoscope: "⊙",
		train: "▥",
		car: "▰",
		fuel: "◉",
		book: "▤",
		film: "▦",
		ticket: "▭",
		home: "⌂",
		paw: "✣",
		gift: "▧",
	};
	return (
		<span
			className={`symbol-mark ${locked ? "is-locked" : ""}`}
			aria-hidden="true"
		>
			{glyph[symbolId] ?? "·"}
		</span>
	);
}

function AppHeader({
	channel,
	title = "오늘의 표본",
	help,
}: {
	channel: Channel;
	title?: string;
	/** Help answer id (help-content.ts) opened by the "?" link. */
	help?: string;
}) {
	const currentPath = window.location.pathname;
	return (
		<header className="app-header" data-channel={channel}>
			{channel === "web" ? (
				<a
					className="back-link"
					href="/"
					onClick={(event) => {
						event.preventDefault();
						navigate("/");
					}}
				>
					‹ 소비도감
				</a>
			) : (
				<span aria-hidden="true" />
			)}
			<span className="app-title">{title}</span>
			<a
				className="icon-button help-button"
				title={help ? "기록하는 길·도움말" : "기록하는 길"}
				href="/app/entry"
				aria-label="기록하는 길"
				onClick={(event) => {
					event.preventDefault();
					navigate("/app/entry");
				}}
			>
				<Icon name="help" />
			</a>
			<a
				className="icon-button"
				href="/app/settings"
				aria-label="설정"
				aria-current={currentPath === "/app/settings" ? "page" : undefined}
				onClick={(event) => {
					event.preventDefault();
					navigate("/app/settings");
				}}
			>
				⚙
			</a>
		</header>
	);
}

function DogamGrid({
	visitCounts,
	onCellClick,
	limit,
}: {
	visitCounts: Readonly<Record<string, number>>;
	onCellClick?: (id: string) => void;
	limit?: number;
}) {
	const visibleCategories = limit ? categories.slice(0, limit) : categories;
	return (
		<fieldset className="dogam-grid">
			<legend className="sr-only">128칸 소비 도감</legend>
			{visibleCategories.map((category) => {
				const count = visitCounts[category.categoryId] ?? 0;
				const stage = unlockStage(count);
				return (
					<button
						className={`dogam-cell stage-${stage}`}
						key={category.categoryId}
						type="button"
						onClick={() => onCellClick?.(category.categoryId)}
						aria-label={`${category.nameKo}, ${stage === 0 ? "미해금" : "해금"}`}
					>
						<span className="cell-head">
							<span className="cell-serial">
								{String(category.sortOrder).padStart(3, "0")}
							</span>
							<span className="cell-stage">
								{stage === 0
									? "미수집"
									: stage === 1
										? "1회"
										: stage === 2
											? "3회"
											: "8회+"}
							</span>
						</span>
						<img
							className={`cell-illustration ${stage === 0 ? "is-locked" : ""}`}
							src={`/illustrations/${category.categoryId}.svg`}
							alt=""
							aria-hidden="true"
							loading="lazy"
						/>
						<strong>{category.nameKo}</strong>
					</button>
				);
			})}
		</fieldset>
	);
}

/** Attribution without identifiers: utm_source and the referrer's host only. */
function landingSource(): { utmSource?: string; referrerHost?: string } {
	const result: { utmSource?: string; referrerHost?: string } = {};
	try {
		const utm = new URLSearchParams(window.location.search).get("utm_source");
		if (utm) result.utmSource = utm.slice(0, 40);
		if (document.referrer) {
			const host = new URL(document.referrer).host;
			if (host && host !== window.location.host) result.referrerHost = host;
		}
	} catch {
		// Attribution is optional.
	}
	return result;
}

function PublicNotFound({ channel }: { channel: Channel }) {
	return (
		<>
			<Header channel={channel} />
			<main className="section-wrap legal-page">
				<p className="eyebrow">찾을 수 없어요</p>
				<h1>없는 주소예요</h1>
				<p className="page-lede">
					주소가 바뀌었거나 잘못 들어왔어요. 도감 전체나 첫 화면에서 다시 찾아
					주세요.
				</p>
				<a
					className="button"
					href="/dogam"
					onClick={(event) => {
						event.preventDefault();
						navigate("/dogam");
					}}
				>
					도감 전체 보기
				</a>
			</main>
			<SiteFooter />
		</>
	);
}

function Onboarding({ channel }: { channel: Channel }) {
	const [step, setStep] = useState(1);
	useEffect(() => {
		analytics.track("onboarding_step", { step, skipped: false });
	}, [step]);
	const finish = (path: string, skipped: boolean) => {
		window.localStorage.setItem("sobidogam:onboarded", "true");
		if (skipped) analytics.track("onboarding_step", { step, skipped: true });
		navigate(path);
	};
	const copy = [
		{
			title: "영수증이 없어도 충분해요",
			body: "결제 알림을 붙여 넣거나 다른 가계부 기록·결제 화면을 가져오면 표본으로 정리해요.",
		},
		{
			title: "마지막 선택은 내 손으로",
			body: "AI가 읽어도 저장 전 확인하고 직접 고칠 수 있어요.",
		},
		{
			title: "모은 표본은 내 기기에",
			body: "가입 없이 시작하고, 전체 기록 백업과 복구코드로 기록을 지켜요.",
		},
	][step - 1] ?? {
		title: "첫 표본을 남겨요",
		body: "남아 있는 결제 흔적 하나로 도감을 시작해요.",
	};
	return (
		<>
			<AppHeader channel={channel} title="처음 만나는 소비도감" />
			<main className="app-main onboarding">
				<div className="onboarding-mark">
					<span>0{step}</span>
					<div className="progress-dots">
						{[1, 2, 3].map((item) => (
							<span className={item <= step ? "active" : ""} key={item} />
						))}
					</div>
				</div>
				<p className="eyebrow">첫 기록</p>
				<h1>{copy.title}</h1>
				<p className="page-lede">{copy.body}</p>
				<div className="onboarding-art">
					<SymbolMark
						symbolId={step === 1 ? "cup" : step === 2 ? "sparkle" : "book"}
					/>
				</div>
				<button
					className="button full-width"
					type="button"
					onClick={() =>
						step < 3 ? setStep(step + 1) : finish("/app/capture", false)
					}
				>
					{step < 3 ? "다음" : "첫 표본 남기기"} <span>→</span>
				</button>
				<button
					className="quiet-button"
					type="button"
					onClick={() => finish("/app", true)}
				>
					건너뛰고 둘러보기
				</button>
			</main>
		</>
	);
}

function AppHome({
	channel,
	receipts,
	household,
	unlock,
	onUnlockClose,
	onHouseholdChange,
	bridge,
	closes,
	onCloseDay,
	onRecord,
	onAllowance,
	recordBusy,
	recordError,
}: {
	channel: Channel;
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	unlock?: UnlockState | null;
	onUnlockClose?: () => void;
	onHouseholdChange?: (next: HouseholdConsumptionState) => Promise<boolean>;
	bridge?: ChannelBridge | undefined;
	closes: DayCloseRecord[];
	onCloseDay: (date: string, quick?: boolean) => void;
	onRecord: () => void;
	onAllowance: () => void;
	recordBusy: boolean;
	recordError: string;
}) {
	const forecasts = forecastRestock(household.items, receipts)
		.filter(
			(f) =>
				!household.shopping.some(
					(e) => e.itemId === f.itemId && e.status === "pending",
				),
		)
		.slice(0, 3);
	return (
		<>
			<AppHeader channel={channel} title="오늘" />
			<main className="app-main today-main">
				<TodayScreen
					receipts={receipts}
					household={household}
					closes={closes}
					onRecord={onRecord}
					onCloseDay={onCloseDay}
					onAllowance={onAllowance}
					busy={recordBusy}
					error={recordError}
				/>
				<MonthCloseCard receipts={receipts} household={household} />
				{forecasts.length ? (
					<section className="home-restock">
						<h2>이번 주 살 것</h2>
						{forecasts.map((f) => (
							<p className="tag-band" key={f.itemId}>
								<span>
									{f.name} ·{" "}
									{f.daysLeft <= 0 ? "떨어질 때예요" : `${f.daysLeft}일 뒤쯤`}
								</span>
								<button
									type="button"
									onClick={() => {
										const at = new Date().toISOString();
										void onHouseholdChange?.(
											queueRestock(household, {
												itemId: f.itemId,
												name: f.name,
												at,
												entryId: `forecast:${f.itemId}:${at}`,
											}),
										);
									}}
								>
									장보기에 담기
								</button>
							</p>
						))}
					</section>
				) : null}
				<OnThisDay receipts={receipts} />
			</main>
			{unlock && onUnlockClose ? (
				<UnlockMoment
					unlock={unlock}
					onClose={onUnlockClose}
					channel={channel}
					bridge={bridge}
				/>
			) : null}
			<AppNav active="home" />
		</>
	);
}

export interface UnlockState {
	categoryId: string;
	count: number;
	amount?: number | undefined;
}

function UnlockMoment({
	unlock,
	onClose,
	channel,
	bridge,
}: {
	unlock: UnlockState;
	onClose: () => void;
	channel: Channel;
	bridge?: ChannelBridge | undefined;
}) {
	const category = categories.find(
		(item) => item.categoryId === unlock.categoryId,
	);
	const titleId = useId();
	const [holding, setHolding] = useState(false);
	const [showAmount, setShowAmount] = useState(false);
	const [notice, setNotice] = useState("");
	useEffect(() => {
		if (holding) return;
		const timer = window.setTimeout(onClose, 4000);
		return () => window.clearTimeout(timer);
	}, [onClose, holding]);
	if (!category) return null;
	const canShare = !category.sensitive;
	return (
		<div
			className="unlock-overlay"
			role="dialog"
			aria-modal="true"
			aria-labelledby={titleId}
			onPointerDown={() => setHolding(true)}
		>
			<div className="unlock-card">
				<span className="unlock-stamp">새 표본 수집</span>
				<img
					className="unlock-illustration"
					src={`/illustrations/${category.categoryId}.svg`}
					alt=""
					aria-hidden="true"
				/>
				<p className="eyebrow">새 표본</p>
				<h2 id={titleId}>새 칸이 열렸어요</h2>
				<p aria-live="polite">
					{category.nameKo} · {unlock.count}번째로 모은 칸(128칸 중)
				</p>
				{canShare ? (
					<>
						{unlock.amount !== undefined ? (
							<label className="unlock-amount-toggle">
								<input
									type="checkbox"
									checked={showAmount}
									onChange={(event) => {
										setHolding(true);
										setShowAmount(event.target.checked);
									}}
								/>
								카드에 금액 보이기
							</label>
						) : null}
						<button
							className="button full-width"
							type="button"
							onClick={async () => {
								setHolding(true);
								const illustration = await loadImage(
									`/illustrations/${category.categoryId}.svg`,
								);
								const blob = await canvasBlob(
									renderUnlockCard(
										{
											categoryName: category.nameKo,
											slug: category.slug,
											cellNumber: unlock.count,
											totalCells: catalog.categories.length,
											amount: unlock.amount,
											illustration,
										},
										{ showAmount },
									),
								);
								const outcome = await shareCard({
									blob,
									filename: `sobidogam-${category.slug}.png`,
									text: `소비도감에서 ${category.nameKo} 칸을 열었어요`,
									webUrl: `https://sobidogam.com/dogam/${category.slug}?utm_source=share&utm_medium=card`,
									tossPath: "/app/dogam",
									channel,
									bridge,
									download: downloadBlob,
								});
								reportCardShare("unlock", outcome, channel);
								setNotice(cardShareNotice(outcome));
							}}
						>
							자랑하기
						</button>
					</>
				) : null}
				{notice ? <output className="form-note">{notice}</output> : null}
				<button className="quiet-button" type="button" onClick={onClose}>
					{holding ? "닫기" : "도감으로 돌아가기"}
				</button>
			</div>
		</div>
	);
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
	return new Promise((resolve) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => resolve(null);
		image.src = src;
	});
}

function reportCardShare(
	kind: "unlock" | "report" | "month_home" | "month_under",
	outcome: CardShareOutcome,
	channel: Channel,
) {
	if (outcome === "cancelled" || outcome === "failed") return;
	analytics.track("share_sent", {
		kind,
		method:
			outcome === "downloaded"
				? "download"
				: outcome === "link_shared"
					? "link"
					: "native",
		channel,
	});
	analytics.track("share_card_create", { kind });
}

function cardShareNotice(outcome: CardShareOutcome): string {
	if (outcome === "downloaded")
		return "카드 이미지를 저장했어요. 사진첩에서 골라 보내 주세요.";
	if (outcome === "link_shared") return "링크를 보냈어요.";
	if (outcome === "failed") return "보내지 못했어요. 잠시 뒤 다시 눌러 주세요.";
	return "";
}

function AppDogam({
	receipts,
	channel,
	household,
	closes,
	onCloseDay,
	bridge,
}: {
	receipts: Receipt[];
	channel: Channel;
	household: HouseholdConsumptionState;
	closes: DayCloseRecord[];
	onCloseDay: (date: string, quick?: boolean) => void;
	bridge?: ChannelBridge | undefined;
}) {
	const [branch, setBranch] = useState<"cells" | "days">(
		new URLSearchParams(window.location.search).get("tab") === "days"
			? "days"
			: "cells",
	);
	const [card, setCard] = useState<{
		kind: "month_home" | "month_under";
		anchor: Date;
	} | null>(null);
	const [showAmount, setShowAmount] = useState(false);
	const [notice, setNotice] = useState("");
	const [busy, setBusy] = useState(false);
	const visits = deriveCategoryVisits(receipts);
	const exportCard = async () => {
		if (!card || busy) return;
		setBusy(true);
		setNotice("");
		try {
			const plan = resolveLivingBudgetPlan(
				household,
				card.anchor,
				monthStartDayPref(),
			);
			const result = deriveDayDogam(
				receipts,
				plan,
				household.recurringPayments ?? [],
				closes,
				card.anchor,
			);
			const privateDates = new Set(
				receipts
					.filter((r) => !r.deletedAt && r.sensitiveMasked)
					.map((r) => kstDateKey(r.transactedAt)),
			);
			const days = result.days.filter((d) => !privateDates.has(d.date));
			const dayCount = days.filter((d) =>
				card.kind === "month_home"
					? Boolean(d.close?.homeItemIds.length)
					: d.kind !== "close_only",
			).length;
			const period = `${formatKstDate(result.period.start).replace(/ \(.*\)$/, "")} ~ ${formatKstDate(new Date(result.period.end.getTime() - 1)).replace(/ \(.*\)$/, "")}`;
			const totalAmount = receipts
				.filter(
					(r) =>
						!r.deletedAt &&
						!r.sensitiveMasked &&
						inPeriod(r.transactedAt, {
							from: result.period.start,
							to: result.period.end,
						}),
				)
				.reduce((sum, r) => sum + r.totalAmount, 0);
			const blob = await canvasBlob(
				renderMonthDayCard(
					{ kind: card.kind, period, dayCount, totalAmount },
					{ showAmount },
				),
			);
			if (!blob) throw new Error("canvas");
			const outcome = await shareCard({
				blob,
				filename: `sobidogam-${result.period.key}-${card.kind}.png`,
				text: `${period} ${card.kind === "month_home" ? "집에 있는 걸로 버틴 날" : "적게 쓴 날"} ${dayCount}일`,
				webUrl: "https://sobidogam.com/?utm_source=share&utm_medium=month_card",
				tossPath: "/app/dogam?tab=days",
				channel,
				bridge,
				download: downloadBlob,
			});
			reportCardShare(card.kind, outcome, channel);
			setNotice(cardShareNotice(outcome));
		} catch {
			setNotice(
				"카드 그림을 만들지 못했어요. 기록은 그대로예요. 다시 눌러 주세요.",
			);
		} finally {
			setBusy(false);
		}
	};
	return (
		<>
			<AppHeader channel={channel} title="내 도감" />
			<main className="app-main">
				<h1>모은 도감</h1>
				<fieldset className="filter-pills" aria-label="도감 갈래">
					<button
						className={branch === "cells" ? "active" : ""}
						type="button"
						aria-pressed={branch === "cells"}
						onClick={() => setBranch("cells")}
					>
						칸
					</button>
					<button
						className={branch === "days" ? "active" : ""}
						type="button"
						aria-pressed={branch === "days"}
						onClick={() => setBranch("days")}
					>
						날
					</button>
				</fieldset>
				{branch === "cells" ? (
					<>
						<p>한 칸에 여러 번 기록하면 모습이 더 또렷해져요.</p>
						<DogamGrid
							visitCounts={visits}
							onCellClick={(id) =>
								navigate(
									`/dogam/${categories.find((c) => c.categoryId === id)?.slug ?? id}`,
								)
							}
						/>
						<button
							className="quiet-button"
							type="button"
							onClick={() => navigate("/app/records")}
						>
							자주 간 매장은 내역에서 보기
						</button>
					</>
				) : (
					<DayDogam
						receipts={receipts}
						household={household}
						closes={closes}
						onCloseDay={onCloseDay}
						onShare={(kind, anchor) => {
							setShowAmount(false);
							setNotice("");
							setCard({ kind, anchor });
						}}
					/>
				)}
			</main>
			<AppNav active="dogam" />
			{card ? (
				<Sheet
					title="이번 달 카드"
					onClose={() => setCard(null)}
					footer={
						<button
							type="button"
							className="button full-width"
							disabled={busy}
							onClick={() => void exportCard()}
						>
							{busy ? "그림 만드는 중…" : "그림으로 보내기·저장"}
						</button>
					}
				>
					<p>
						{card.kind === "month_home"
							? "집에 있는 걸로 버틴 날"
							: "적게 쓴 날"}
						을 한 장으로 만들어요. 건강 관련 업종이 기록된 날은 카드에서 빼요.
					</p>
					<label className="check-row">
						<input
							type="checkbox"
							checked={showAmount}
							onChange={(e) => setShowAmount(e.target.checked)}
						/>
						금액 보이기
					</label>
					<p className="field-hint">빈 날짜나 물건 이름은 싣지 않아요.</p>
					{notice ? <output>{notice}</output> : null}
				</Sheet>
			) : null}
		</>
	);
}

async function requestExtraction(
	file: File,
	channel: Channel,
	apiBaseUrl: string,
	source: "ai" | "screenshot" = "ai",
	identity?: IdentityPort,
): Promise<Draft | null> {
	const form = new FormData();
	const requestId = crypto.randomUUID();
	const startedAt = performance.now();
	form.append("requestId", requestId);
	// A local filename can include a merchant or person's name. It is neither
	// analytics data nor request metadata, so use a fixed multipart filename.
	form.append("image", file, "receipt-image");
	analytics.track("receipt_extract_start", {});
	const response = await fetch(
		`${apiBaseUrl.replace(/\/$/, "")}/api/v1/receipts/extract`,
		{
			method: "POST",
			headers: await installHeaders(channel, identity),
			body: form,
		},
	).catch(() => null);
	if (!response) {
		rememberError("E-NETWORK");
		analytics.track("receipt_extract_failed", {
			code: "NETWORK",
			elapsedMs: Math.round(performance.now() - startedAt),
		});
		return null;
	}
	if (!response.ok) {
		rememberError(`HTTP_${response.status}`);
		analytics.track("receipt_extract_failed", {
			code: `HTTP_${response.status}`,
			elapsedMs: Math.round(performance.now() - startedAt),
		});
		return null;
	}
	const body = (await response.json()) as {
		ok?: boolean;
		data?: {
			storeDisplay?: string;
			totalAmount?: number | null;
			categoryHint?: string | null;
			transactedAt?: string | null;
			items?: Draft["items"];
			confidence?: { store?: number; amount?: number };
			modelId?: string;
		};
	};
	if (!body.ok || !body.data) return null;
	analytics.track("receipt_extract_done", {
		elapsedMs: Math.round(performance.now() - startedAt),
		confidenceStore: body.data.confidence?.store ?? 0,
		confidenceAmount: body.data.confidence?.amount ?? 0,
		modelId: body.data.modelId ?? "unknown",
	});
	return {
		storeDisplay: body.data.storeDisplay || "확인 필요",
		totalAmount: body.data.totalAmount ?? 0,
		categoryId: body.data.categoryHint ?? "etc-other",
		transactedAt: body.data.transactedAt ?? new Date().toISOString(),
		source,
		items: body.data.items ?? [],
		aiRead: true,
	};
}

function Capture({
	entryNotice,
	onDrafts,
	receipts,
	household,
	onManualSave,
	channel,
	apiBaseUrl,
	identity,
	bridge,
}: {
	entryNotice?: string;
	onDrafts: (drafts: Draft[], notice?: string) => void;
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	onManualSave: (draft: Draft) => Promise<boolean>;
	channel: Channel;
	apiBaseUrl: string | undefined;
	identity?: IdentityPort | undefined;
	bridge?: ChannelBridge | undefined;
}) {
	const [busy, setBusy] = useState(false);
	const search = new URLSearchParams(window.location.search);
	const categorySlug = search.get("category");
	const presetCategory = categorySlug
		? categories.find(
				(category) => category.slug === categorySlug && !category.sensitive,
			)
		: undefined;
	const [manual, setManual] = useState(
		search.get("mode") === "manual" || Boolean(presetCategory),
	);
	const features = useServerFeatures(apiBaseUrl);
	const [error, setError] = useState(entryNotice ?? "");
	const [errorTarget, setErrorTarget] = useState<"notification" | null>(null);
	const [notificationText, setNotificationText] = useState("");
	const [genericCsv, setGenericCsv] = useState<string | null>(null);
	const notificationInputRef = useRef<HTMLTextAreaElement>(null);
	const notificationTitleId = useId();
	const captureErrorId = useId();
	useEffect(() => {
		markCaptureStarted();
	}, []);
	// Outside entry: iPhone shortcut (#paste=…) or Android share sheet (#shared).
	// The text lives only in the URL fragment or a same-origin cache and is
	// removed from both as soon as it is read.
	// biome-ignore lint/correctness/useExhaustiveDependencies: runs once per entry
	useEffect(() => {
		const consume = () => {
			const hash = window.location.hash;
			if (search.get("from") === "share") {
				setManual(false);
				showNotificationError(
					"공유한 문자를 받지 못했어요. 홈 화면에 추가한 뒤 다시 공유하거나, 아래 칸에 붙여 넣어 주세요.",
				);
				return;
			}
			if (hash.startsWith("#paste=")) {
				let text = "";
				try {
					text = decodeURIComponent(hash.slice("#paste=".length));
				} catch {
					text = "";
				}
				window.history.replaceState({}, "", window.location.pathname);
				setManual(false);
				if (!text.trim()) return;
				setNotificationText(text);
				setPendingCaptureMode("deeplink");
				openNotificationDrafts(text);
				return;
			}
			if (hash === "#shared" && "caches" in window) {
				window.history.replaceState({}, "", window.location.pathname);
				setManual(false);
				void (async () => {
					const cache = await caches.open("sobidogam-share");
					const response = await cache.match("/__share/latest");
					await cache.delete("/__share/latest");
					const text = response ? await response.text() : "";
					if (!text.trim()) {
						showNotificationError(
							"공유한 내용에서 결제 문자를 찾지 못했어요. 아래 칸에 붙여 넣어 주세요.",
						);
						return;
					}
					setNotificationText(text);
					setPendingCaptureMode("share_target");
					openNotificationDrafts(text);
				})().catch(() =>
					showNotificationError(
						"공유한 내용을 읽지 못했어요. 아래 칸에 붙여 넣어 주세요.",
					),
				);
			}
		};
		consume();
		window.addEventListener("hashchange", consume);
		return () => window.removeEventListener("hashchange", consume);
	}, []);

	const clearError = () => {
		setError("");
		setErrorTarget(null);
	};
	const showNotificationError = (message: string) => {
		setError(message);
		setErrorTarget("notification");
		requestAnimationFrame(() => notificationInputRef.current?.focus());
	};
	const showGeneralError = (message: string) => {
		setError(message);
		setErrorTarget(null);
	};
	const openNotificationDrafts = (value: string) => {
		analytics.track("capture_start", { mode: "notification" });
		const parsed = parsePaymentNotificationText(value);
		if (!parsed.records.length) {
			showNotificationError(
				"결제 금액을 찾지 못했어요. 알림 전체를 붙여 넣거나 직접 입력해 주세요.",
			);
			return;
		}
		clearError();
		const drafts = parsed.records.map((record) => {
			const normalized = normalizeStoreName(record.storeDisplay);
			const inferred = classifyWithRules({
				storeKey: normalized.key,
				aiCategoryHint: null,
				catalog,
				rules: readStoreRules(),
			});
			return {
				storeDisplay: record.storeDisplay,
				totalAmount: record.totalAmount,
				categoryId: inferred.categoryId,
				transactedAt: record.transactedAt,
				source: record.source,
				items: [],
				warnings: record.warnings,
				notificationPreview: parsed.records.length === 1 ? value : undefined,
			} satisfies Draft;
		});
		analytics.track("payment_trace_parsed", {
			source: "notification",
			recordCount: drafts.length,
			rejectedCount: parsed.rejected.length,
		});
		onDrafts(
			drafts,
			parsed.rejected.length
				? `취소·환불 또는 금액이 없는 ${parsed.rejected.length}건은 제외했어요.`
				: undefined,
		);
	};
	const readClipboard = async () => {
		analytics.track("capture_start", { mode: "clipboard" });
		try {
			const value = bridge?.readClipboard
				? await bridge.readClipboard()
				: await navigator.clipboard.readText();
			if (value === null) throw new Error("denied");
			if (!value.trim()) throw new Error("empty");
			setNotificationText(value);
			openNotificationDrafts(value);
		} catch {
			showNotificationError(
				"클립보드를 읽을 수 없어요. 아래 칸을 길게 눌러 직접 붙여 넣어 주세요.",
			);
		}
	};
	const csvDrafts = (records: CsvPaymentDraft[]) =>
		records.map((record) => {
			const combined = normalizeStoreName(
				`${record.storeDisplay} ${record.categoryLabel ?? ""}`,
			);
			const inferred = classifyWithRules({
				storeKey: combined.key,
				aiCategoryHint: null,
				catalog,
				rules: readStoreRules(),
			});
			return {
				storeDisplay: record.storeDisplay,
				totalAmount: record.totalAmount,
				categoryId: inferred.categoryId,
				transactedAt: record.transactedAt,
				source: "csv" as const,
				items: [],
				warnings: record.warnings,
			};
		});
	const pickCsv = (event: ChangeEvent<HTMLInputElement>, generic = false) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;
		analytics.track("capture_start", { mode: "csv" });
		setBusy(true);
		clearError();
		void file
			.text()
			.then((text) => {
				const parsed = generic
					? { records: [], rejected: [] }
					: importPaymentCsv(text);
				if (!parsed.records.length) {
					// Unknown column names: let the user pick the columns (#46).
					setGenericCsv(text);
					return;
				}
				const drafts = csvDrafts(parsed.records);
				analytics.track("payment_trace_parsed", {
					source: "csv",
					recordCount: drafts.length,
					rejectedCount: parsed.rejected.length,
				});
				analytics.track("import_run", {
					format: "csv",
					recordCount: drafts.length,
				});
				onDrafts(
					drafts,
					parsed.rejected.length
						? `수입·이체·금액 없는 ${parsed.rejected.length}행은 제외했어요.`
						: undefined,
				);
			})
			.catch(() =>
				showGeneralError(
					"파일을 읽지 못했어요. 엑셀에서 'CSV UTF-8'로 저장한 파일인지 확인해 주세요.",
				),
			)
			.finally(() => setBusy(false));
	};
	const importGeneric = (records: CsvPaymentDraft[], dateRows: number[]) => {
		setGenericCsv(null);
		const existing = receipts.filter((receipt) => !receipt.deletedAt);
		const drafts = csvDrafts(records).filter(
			(draft) =>
				!findDuplicate(
					{
						receiptId: "draft",
						storeKey: normalizeStoreName(draft.storeDisplay).key,
						totalAmount: draft.totalAmount,
						transactedAt: draft.transactedAt,
					},
					existing,
				),
		);
		const skipped = records.length - drafts.length;
		analytics.track("import_run", {
			format: "csv_generic",
			recordCount: drafts.length,
		});
		const notes = [
			skipped ? `이미 있는 ${skipped}건은 건너뛰었어요.` : "",
			dateRows.length ? `${dateRows.length}행은 날짜를 못 읽어 뺐어요.` : "",
		].filter(Boolean);
		if (!drafts.length) {
			showGeneralError(
				skipped
					? `새로 가져올 기록이 없어요. ${skipped}건은 이미 있어요.`
					: "가져올 기록을 찾지 못했어요.",
			);
			return;
		}
		onDrafts(drafts, notes.join(" ") || undefined);
	};
	const pickImages = (event: ChangeEvent<HTMLInputElement>) => {
		const files = [...(event.target.files ?? [])].slice(0, 10);
		if (!files.length) return;
		analytics.track("capture_start", { mode: "album" });
		if (
			files.some(
				(file) =>
					!file.type.startsWith("image/") && file.type !== "application/pdf",
			)
		) {
			showGeneralError("JPG·PNG·WEBP·PDF 파일만 가져올 수 있어요.");
			return;
		}
		setBusy(true);
		clearError();
		void (async () => {
			try {
				const drafts = await Promise.all(
					files.map(async (file) => {
						// Photo reading is off in production: never upload a photo the
						// server cannot read (complete-services 4-8).
						const remote =
							apiBaseUrl && features.receiptAi && file.type.startsWith("image/")
								? await requestExtraction(
										file,
										channel,
										apiBaseUrl,
										"screenshot",
										identity,
									)
								: null;
						const fallback: Draft = {
							storeDisplay: "확인 필요",
							totalAmount: 0,
							categoryId: "etc-other",
							transactedAt: new Date().toISOString(),
							source: "screenshot" as const,
							items: [],
							warnings: ["merchant", "amount"],
						};
						return remote ?? fallback;
					}),
				);
				analytics.track("payment_trace_parsed", {
					source: "screenshot",
					recordCount: drafts.length,
					rejectedCount: 0,
				});
				onDrafts(
					drafts,
					!features.receiptAi
						? "사진 자동 읽기는 아직 준비 중이에요. 사진을 보며 매장과 금액을 넣어 주세요. 사진은 서버로 보내지 않았어요."
						: drafts.some((draft) => draft.totalAmount === 0)
							? "읽지 못한 파일은 매장과 금액을 직접 확인해 주세요. 원본 파일은 저장하지 않아요."
							: undefined,
				);
			} catch {
				showGeneralError(
					"읽기에 실패했어요. 파일을 다시 고르거나 직접 입력으로 이어가 주세요.",
				);
			} finally {
				setBusy(false);
			}
		})();
	};
	return (
		<>
			<AppHeader channel={channel} title="결제 기록" help="paste" />
			<main className="app-main capture-page">
				<div className="page-intro compact-intro">
					<h1>결제 한 건 남기기</h1>
					<p>
						{manual
							? "매장과 금액만 적으면 돼요. 분류는 매장 이름으로 먼저 골라 둘게요."
							: "카드·간편결제 알림을 복사했다면 아래 버튼 한 번이면 돼요."}
					</p>
				</div>
				{error && (
					<div
						className="notice notice-danger"
						id={captureErrorId}
						role="alert"
					>
						{error}
					</div>
				)}
				{busy ? (
					<div className="loading-card">
						<span className="loading-spinner" />
						파일을 살펴보고 있어요…
					</div>
				) : (
					<>
						<fieldset className="task-tabs" aria-label="기록 방법">
							<button
								type="button"
								aria-pressed={!manual}
								onClick={() => setManual(false)}
							>
								알림 붙여 넣기
							</button>
							<button
								type="button"
								aria-pressed={manual}
								onClick={() => setManual(true)}
							>
								직접 입력
							</button>
						</fieldset>
						<div hidden={!manual}>
							<QuickRecord
								receipts={receipts}
								household={household}
								onSave={onManualSave}
								initialCategoryId={presetCategory?.categoryId}
							/>
						</div>
						<div hidden={manual}>
							<section
								className="notification-import"
								aria-labelledby={notificationTitleId}
							>
								<button
									className="button full-width paste-primary"
									type="button"
									onClick={() => void readClipboard()}
								>
									클립보드에서 붙여 넣기
								</button>
								<p className="paste-label" id={notificationTitleId}>
									또는 여기에 직접 붙여 넣기
									<small>여러 건은 빈 줄로 나눠요 · 이 기기에서만 읽어요</small>
								</p>
								<textarea
									ref={notificationInputRef}
									value={notificationText}
									onChange={(event) => setNotificationText(event.target.value)}
									placeholder={"예: [카드] 12,300원 승인 10/16 08:42 동네카페"}
									aria-labelledby={notificationTitleId}
									aria-invalid={
										errorTarget === "notification" && error ? "true" : undefined
									}
									aria-describedby={
										errorTarget === "notification" && error
											? captureErrorId
											: undefined
									}
									aria-errormessage={
										errorTarget === "notification" && error
											? captureErrorId
											: undefined
									}
								/>
								<div className="import-actions">
									<button
										className="button button-small"
										type="button"
										disabled={!notificationText.trim()}
										onClick={() => openNotificationDrafts(notificationText)}
									>
										결제 내용 확인
									</button>
								</div>
							</section>
							<div className="import-grid">
								<label className="capture-drop compact">
									<input
										type="file"
										accept="image/jpeg,image/png,image/webp,application/pdf"
										multiple
										onChange={pickImages}
									/>
									<span className="capture-icon">▧</span>
									<strong>스크린샷·전자영수증</strong>
									<small>이미지·PDF 최대 10개</small>
								</label>
								<label className="capture-drop compact">
									<input
										type="file"
										accept=".csv,text/csv"
										onChange={(event) => pickCsv(event)}
									/>
									<span className="capture-icon">↓</span>
									<strong>다른 가계부 기록 옮기기</strong>
									<small>위플·편한가계부에서 내려받은 기록</small>
								</label>
							</div>
							<label className="capture-generic">
								<input
									type="file"
									accept=".csv,text/csv"
									onChange={(event) => pickCsv(event, true)}
								/>
								다른 형식(은행·엑셀 표)은 열을 골라 가져오기 →
							</label>
							{genericCsv !== null ? (
								<GenericCsvImport
									text={genericCsv}
									onCancel={() => setGenericCsv(null)}
									onImport={importGeneric}
								/>
							) : null}
							<label className="capture-drop receipt-option">
								<input
									type="file"
									accept="image/jpeg,image/png,image/webp"
									capture="environment"
									onChange={pickImages}
								/>
								<span className="capture-icon">＋</span>
								<strong>종이 영수증 촬영 (선택)</strong>
								<small>품목을 더 자세히 남기고 싶을 때</small>
							</label>
							<p className="ai-notice">
								{features.receiptAi
									? "사진의 글자를 AI가 읽어요. 사진은 읽은 뒤 바로 지워요."
									: "사진 자동 읽기는 준비 중이에요. 사진은 서버로 보내지 않고, 사진을 보며 직접 확인하게 도와 드려요."}
							</p>
						</div>
					</>
				)}
			</main>
			<AppNav active="capture" />
		</>
	);
}

export interface Draft {
	storeDisplay: string;
	totalAmount: number;
	categoryId: string;
	transactedAt: string;
	source: PaymentTraceSource;
	items: { name: string; amount: number | null; qty: number | null }[];
	tagIds?: string[];
	memo?: string | null;
	mood?: MemoryMood | null;
	occasion?: MemoryOccasion | null;
	isFavorite?: boolean;
	purchaseReason?: PurchaseReason | null;
	expectedScene?: string | null;
	warnings?: ("merchant" | "amount" | "date" | "category")[];
	recurringPaymentId?: string | null;
	/** Values were read by the server AI and must be shown as such (AI 기본법 제31조). */
	aiRead?: boolean;
	/** In-memory confirmation only; never copied into a Receipt or telemetry. */
	notificationPreview?: string | undefined;
}

function Confirm({
	household,
	drafts,
	onSave,
	channel,
	notice,
	receipts,
}: {
	household: HouseholdConsumptionState;
	drafts: Draft[];
	onSave: (drafts: Draft[]) => Promise<boolean>;
	channel: Channel;
	notice?: string | undefined;
	receipts: Receipt[];
}) {
	const [saveBusy, setSaveBusy] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [rows, setRows] = useState(() =>
		drafts.map((draft) => ({
			id: crypto.randomUUID(),
			included: true,
			duplicateConfirmed: false,
			draft,
			initialCategoryId: draft.categoryId,
			rememberRule: true,
		})),
	);
	const [tags, setTags] = useState<TagDef[]>(readTags);
	const [newTag, setNewTag] = useState("");
	const update = (id: string, patch: Partial<Draft>) =>
		setRows((current) =>
			current.map((row) =>
				row.id === id
					? {
							...row,
							duplicateConfirmed:
								"storeDisplay" in patch ||
								"totalAmount" in patch ||
								"transactedAt" in patch
									? false
									: row.duplicateConfirmed,
							draft: { ...row.draft, ...patch },
						}
					: row,
			),
		);
	const selected = rows.filter((row) => row.included);
	const invalid = selected.some(
		(row) => !row.draft.storeDisplay.trim() || row.draft.totalAmount <= 0,
	);
	const duplicateFor = (draft: Draft) =>
		findDuplicate(
			{
				receiptId: "draft",
				storeKey: normalizeStoreName(draft.storeDisplay).key,
				totalAmount: draft.totalAmount,
				transactedAt: draft.transactedAt,
			},
			receipts.filter((receipt) => !receipt.deletedAt),
		);
	const hasUnconfirmedDuplicate = selected.some(
		(row) => duplicateFor(row.draft) && !row.duplicateConfirmed,
	);
	return (
		<>
			<AppHeader channel={channel} title="결제 내용 확인" />
			<main className="app-main confirm-page">
				<div className="page-intro">
					<p className="eyebrow">저장 전 확인</p>
					<h1>
						{drafts.length > 1
							? `${drafts.length}건을 확인해 주세요.`
							: "결제 내용을 확인해 주세요."}
					</h1>
					<p>저장할 행만 남기고, 매장·금액·분류를 확인해 주세요.</p>
				</div>
				{notice ? <div className="notice notice-sage">{notice}</div> : null}
				{drafts.some((draft) => draft.aiRead) ? (
					<p className="notice ai-result">
						AI가 읽은 값이에요. 매장·금액·날짜를 확인하고 저장해 주세요.
					</p>
				) : null}
				<div className="batch-confirm-list">
					{rows.map((row, index) => {
						const category = categories.find(
							(item) => item.categoryId === row.draft.categoryId,
						);
						const duplicate = duplicateFor(row.draft);
						const duplicateReceipt = duplicate
							? receipts.find(
									(receipt) => receipt.receiptId === duplicate.receiptId,
								)
							: null;
						return (
							<article
								className={`confirm-card batch-row ${row.included ? "" : "is-excluded"}`}
								key={row.id}
							>
								<div className="batch-row-head">
									<span className="confirm-symbol">
										<SymbolMark symbolId={category?.symbolId ?? "store"} />
									</span>
									<div>
										<strong>{index + 1}번째 표본</strong>
										<small>
											{sourceLabel(row.draft.source)} ·{" "}
											{formatKstDateTime(row.draft.transactedAt)}
										</small>
									</div>
									<label className="include-toggle">
										<input
											type="checkbox"
											checked={row.included}
											onChange={(event) =>
												setRows((current) =>
													current.map((item) =>
														item.id === row.id
															? { ...item, included: event.target.checked }
															: item,
													),
												)
											}
										/>
										<span>저장</span>
									</label>
								</div>
								{row.draft.notificationPreview && !category?.sensitive ? (
									<div className="notification-interpretation">
										<details>
											<summary>붙여 넣은 알림</summary>
											<p>{row.draft.notificationPreview}</p>
										</details>
										<p className="field-hint">
											↓ 읽은 내용을 아래에서 확인하고 저장해요
										</p>
									</div>
								) : null}
								{row.draft.warnings?.length ? (
									<p className="row-warning">
										자동으로 확정하지 못한 항목이 있어요. 아래 내용을 확인해
										주세요.
									</p>
								) : null}
								{duplicate ? (
									<div className="notice notice-sage duplicate-notice">
										<strong>비슷한 기록이 있어요.</strong>
										<p>
											{duplicateReceipt?.storeDisplay ?? "기존 기록"} ·{" "}
											{(duplicate.totalAmount ?? 0).toLocaleString("ko-KR")}원 ·{" "}
											{formatKstDateTime(duplicate.transactedAt ?? "")}
										</p>
										<label className="duplicate-confirmation">
											<input
												disabled={!row.included}
												type="checkbox"
												checked={row.duplicateConfirmed}
												onChange={(event) =>
													setRows((current) =>
														current.map((item) =>
															item.id === row.id
																? {
																		...item,
																		duplicateConfirmed: event.target.checked,
																	}
																: item,
														),
													)
												}
											/>
											<span>별개 결제예요. 두 기록을 모두 저장할게요.</span>
										</label>
									</div>
								) : null}
								<label>
									매장 이름
									<input
										disabled={!row.included}
										value={row.draft.storeDisplay}
										onChange={(event) =>
											update(row.id, { storeDisplay: event.target.value })
										}
									/>
								</label>
								<label>
									카테고리
									<select
										disabled={!row.included}
										value={row.draft.categoryId}
										onChange={(event) =>
											update(row.id, { categoryId: event.target.value })
										}
									>
										{categories.map((item) => (
											<option key={item.categoryId} value={item.categoryId}>
												{item.nameKo}
											</option>
										))}
									</select>
								</label>
								{row.draft.categoryId !== row.initialCategoryId &&
								!category?.sensitive ? (
									<label className="check-row">
										<input
											type="checkbox"
											checked={row.rememberRule}
											onChange={(event) =>
												setRows((current) =>
													current.map((item) =>
														item.id === row.id
															? { ...item, rememberRule: event.target.checked }
															: item,
													),
												)
											}
										/>
										<span>
											다음에도 {row.draft.storeDisplay || "이 매장"}은{" "}
											{category?.nameKo ?? "이 분류"}로
										</span>
									</label>
								) : null}
								{!category?.sensitive &&
								[
									"food-grocery",
									"daily-cvs",
									"daily-supermarket",
									"daily-hypermarket",
									"daily-household",
								].includes(row.draft.categoryId)
									? household.shopping
											.filter((e) => e.status === "pending")
											.map((entry) => (
												<label className="check-row" key={entry.entryId}>
													<input
														type="checkbox"
														checked={row.draft.items.some(
															(item) => item.name === entry.name,
														)}
														onChange={(e) =>
															update(row.id, {
																items: e.target.checked
																	? [
																			...row.draft.items,
																			{
																				name: entry.name,
																				amount: null,
																				qty: null,
																			},
																		]
																	: row.draft.items.filter(
																			(item) => item.name !== entry.name,
																		),
															})
														}
													/>
													{entry.name} 샀어요
												</label>
											))
									: null}
								<div className="date-field">
									결제 시각
									<KoreanDateTimeField
										disabled={!row.included}
										value={toKstDateTimeInput(row.draft.transactedAt)}
										onChange={(value) =>
											update(row.id, { transactedAt: `${value}:00+09:00` })
										}
									/>
								</div>
								<label>
									총액
									<input
										disabled={!row.included}
										inputMode="numeric"
										value={String(row.draft.totalAmount || "")}
										onChange={(event) =>
											update(row.id, {
												totalAmount:
													Number(event.target.value.replace(/[^0-9]/g, "")) ||
													0,
											})
										}
									/>
									<small>원</small>
								</label>
								{category?.sensitive ? (
									<div className="notice notice-sage">
										민감 업종이라 품목과 메모는 저장하지 않아요.
									</div>
								) : null}
								<details className="confirm-optional">
									<summary>
										{category?.sensitive
											? "태그 더하기 (선택)"
											: "기억과 태그 더하기 (선택)"}
									</summary>
									{!category?.sensitive ? (
										<div className="memory-fields">
											<fieldset className="reason-picker">
												<legend>왜 고르게 됐나요? (선택)</legend>
												<div>
													{(
														Object.entries(PURCHASE_REASON_LABELS) as [
															PurchaseReason,
															string,
														][]
													).map(([value, label]) => (
														<button
															className={
																row.draft.purchaseReason === value
																	? "active"
																	: ""
															}
															disabled={!row.included}
															key={value}
															type="button"
															onClick={() =>
																update(row.id, {
																	purchaseReason:
																		row.draft.purchaseReason === value
																			? null
																			: value,
																})
															}
														>
															{label}
														</button>
													))}
												</div>
											</fieldset>
											<label>
												어떤 장면을 기대했나요? (선택)
												<input
													disabled={!row.included}
													maxLength={160}
													value={row.draft.expectedScene ?? ""}
													onChange={(event) =>
														update(row.id, {
															expectedScene: event.target.value,
														})
													}
													placeholder="예: 출근 전에 10분이라도 천천히 마시고 싶었다"
												/>
											</label>
											<label>
												이 장면의 한 줄 (선택)
												<textarea
													disabled={!row.included}
													maxLength={200}
													value={row.draft.memo ?? ""}
													onChange={(event) =>
														update(row.id, { memo: event.target.value })
													}
													placeholder="예: 비가 그친 뒤 창가 자리에서 오래 이야기했다"
												/>
											</label>
											<fieldset className="mood-picker">
												<legend>그때의 기분 (선택)</legend>
												<div>
													{(
														Object.entries(MOOD_LABELS) as [
															MemoryMood,
															string,
														][]
													).map(([value, label]) => (
														<button
															className={
																row.draft.mood === value ? "active" : ""
															}
															disabled={!row.included}
															key={value}
															type="button"
															aria-pressed={row.draft.mood === value}
															onClick={() =>
																update(row.id, {
																	mood: row.draft.mood === value ? null : value,
																})
															}
														>
															{label}
														</button>
													))}
												</div>
											</fieldset>
											<label>
												누구와 있었나요? (선택)
												<select
													disabled={!row.included}
													value={row.draft.occasion ?? ""}
													onChange={(event) =>
														update(row.id, {
															occasion: (event.target.value ||
																null) as MemoryOccasion | null,
														})
													}
												>
													<option value="">선택하지 않음</option>
													{(
														Object.entries(OCCASION_LABELS) as [
															MemoryOccasion,
															string,
														][]
													).map(([value, label]) => (
														<option key={value} value={value}>
															{label}
														</option>
													))}
												</select>
											</label>
											<label className="favorite-toggle">
												<input
													disabled={!row.included}
													type="checkbox"
													checked={row.draft.isFavorite ?? false}
													onChange={(event) =>
														update(row.id, { isFavorite: event.target.checked })
													}
												/>
												<span>오래 기억하고 싶은 장면</span>
											</label>
										</div>
									) : null}
									<fieldset className="tag-picker">
										<legend>태그 (선택)</legend>
										<div className="tag-list">
											{tags.map((tag) => {
												const active =
													row.draft.tagIds?.includes(tag.id) ?? false;
												return (
													<button
														className={active ? "tag active" : "tag"}
														disabled={!row.included}
														key={tag.id}
														type="button"
														aria-pressed={active}
														onClick={() =>
															update(row.id, {
																tagIds: active
																	? (row.draft.tagIds ?? []).filter(
																			(id) => id !== tag.id,
																		)
																	: [...(row.draft.tagIds ?? []), tag.id],
															})
														}
													>
														{tag.label}
													</button>
												);
											})}
										</div>
										<div className="tag-add">
											<input
												aria-label="새 태그"
												placeholder="+ 태그 (예: 제주 여행)"
												value={newTag}
												maxLength={20}
												onChange={(event) => setNewTag(event.target.value)}
											/>
											<button
												type="button"
												className="quiet-button"
												disabled={!newTag.trim()}
												onClick={() => {
													const label = newTag
														.normalize("NFKC")
														.trim()
														.slice(0, 20);
													const existing = tags.find(
														(tag) => tag.label === label,
													);
													const tag = existing ?? {
														id: `tag-${crypto.randomUUID()}`,
														label,
													};
													if (!existing) {
														const next = [...tags, tag];
														setTags(next);
														writeTags(next);
													}
													update(row.id, {
														tagIds: [
															...new Set([...(row.draft.tagIds ?? []), tag.id]),
														],
													});
													setNewTag("");
												}}
											>
												더하기
											</button>
										</div>
									</fieldset>
								</details>
							</article>
						);
					})}
				</div>
				<div className="confirm-actions">
					{invalid ? (
						<p className="form-error" role="alert">
							저장할 모든 행의 매장과 1원 이상 금액을 확인해 주세요.
						</p>
					) : null}
					{hasUnconfirmedDuplicate ? (
						<p className="form-error" role="alert">
							비슷한 기록인지 확인한 뒤, 두 기록을 모두 저장할지 직접 선택해
							주세요.
						</p>
					) : null}
					<button
						className="button full-width"
						type="button"
						disabled={
							saveBusy || !selected.length || invalid || hasUnconfirmedDuplicate
						}
						aria-busy={saveBusy}
						onClick={async () => {
							setSaveBusy(true);
							setSaveError("");
							try {
								const rules = selected.reduce((current, row) => {
									const chosen = categories.find(
										(item) => item.categoryId === row.draft.categoryId,
									);
									return row.draft.categoryId !== row.initialCategoryId &&
										row.rememberRule &&
										!chosen?.sensitive
										? setStoreRule(
												current,
												normalizeStoreName(row.draft.storeDisplay).key,
												row.draft.categoryId,
											)
										: current;
								}, readStoreRules());
								writeStoreRules(rules);
								if (!(await onSave(selected.map((row) => row.draft))))
									setSaveError(
										"저장하지 못했어요. 내용을 그대로 두었으니 다시 시도해 주세요.",
									);
							} catch {
								setSaveError(
									"저장하지 못했어요. 내용을 그대로 두었으니 다시 시도해 주세요.",
								);
							} finally {
								setSaveBusy(false);
							}
						}}
					>
						{saveBusy ? "저장 중…" : `${selected.length}건 도감에 넣기`}{" "}
						<span>→</span>
					</button>
					{saveError && (
						<p className="form-error" role="alert">
							{saveError}
						</p>
					)}
					<button
						className="quiet-button"
						type="button"
						disabled={saveBusy}
						onClick={() => navigate("/app/capture")}
					>
						다시 입력
					</button>
				</div>
			</main>
		</>
	);
}

function Records({
	receipts,
	household,
	onDelete,
	onRefund,
	onRestore,
	onUpdate,
	onEdit,
	onBulk,
	channel,
}: {
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	onDelete: (id: string) => Promise<boolean>;
	onRefund: (id: string) => Promise<boolean>;
	onRestore: (id: string) => Promise<boolean>;
	onUpdate: (id: string, patch: Partial<Receipt>) => Promise<boolean>;
	onEdit: (
		id: string,
		patch: RecordEdit,
		rememberRule: boolean,
	) => Promise<boolean>;
	onBulk: (
		ids: string[],
		action: { kind: "category"; categoryId: string } | { kind: "trash" },
	) => Promise<{ ok: number; failed: number }>;
	channel: Channel;
}) {
	const initialSearch = new URLSearchParams(window.location.search);
	const [query, setQuery] = useState(initialSearch.get("q") ?? "");
	const [period, setPeriod] = useState<PeriodChoice>(() => ({
		kind: initialSearch.has("day")
			? "custom"
			: initialSearch.get("period") === "this_month" ||
					initialSearch.get("period") === "last_month"
				? (initialSearch.get("period") as PeriodKind)
				: "all",
		custom: {
			from: initialSearch.get("day") ?? initialSearch.get("from") ?? "",
			to: initialSearch.get("day") ?? initialSearch.get("to") ?? "",
		},
	}));
	const [view, setView] = useState<"list" | "calendar">(
		initialSearch.get("view") === "calendar" ? "calendar" : "list",
	);
	const [selecting, setSelecting] = useState(
		initialSearch.get("select") === "1",
	);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [bulkCategory, setBulkCategory] = useState("");
	const [bulkBusy, setBulkBusy] = useState(false);
	const [fixingId, setFixingId] = useState<string | null>(null);
	const [storeKeyOpen, setStoreKeyOpen] = useState<string | null>(null);
	const tags = readTags();
	const [showTrash, setShowTrash] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(() => {
		return new URLSearchParams(window.location.search).get("reflect");
	});
	const [filter, setFilter] = useState<"all" | "reflect" | "repurchase">("all");
	const [refundNotice, setRefundNotice] = useState<string | null>(null);
	const reflectionIds = new Set(
		findReflectionCandidates(receipts).map((receipt) => receipt.receiptId),
	);
	const resolvedPeriod = showTrash ? null : resolvePeriodChoice(period);
	const needle = query.trim().toLowerCase();
	// #27: search also covers bought item names, notes and tag names.
	const haystack = (receipt: Receipt) =>
		[
			receipt.storeDisplay,
			categories.find((category) => category.categoryId === receipt.categoryId)
				?.nameKo ?? "",
			receipt.memo ?? "",
			receipt.expectedScene ?? "",
			receipt.afterNote ?? "",
			...(receipt.items ?? []).map((item) => item.name),
			...(receipt.tagIds ?? []).map((id) => tagLabel(tags, id)),
		]
			.join(" ")
			.toLowerCase();
	const visible = receipts
		.filter(
			(receipt) =>
				(showTrash
					? Boolean(
							receipt.deletedAt &&
								Date.now() - Date.parse(receipt.deletedAt) <= 30 * 86400000,
						)
					: !receipt.deletedAt) &&
				(filter === "all" ||
					(filter === "reflect" && reflectionIds.has(receipt.receiptId)) ||
					(filter === "repurchase" && receipt.repurchaseIntent === "yes")) &&
				(!resolvedPeriod || inPeriod(receipt.transactedAt, resolvedPeriod)) &&
				(!needle || haystack(receipt).includes(needle)),
		)
		.sort((a, b) => b.transactedAt.localeCompare(a.transactedAt));
	const visibleTotal = visible.reduce(
		(sum, receipt) => sum + Math.max(0, receipt.totalAmount),
		0,
	);
	const toggleSelected = (id: string) =>
		setSelected((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	const runBulk = async (
		action: { kind: "category"; categoryId: string } | { kind: "trash" },
	) => {
		const ids = [...selected].slice(0, 200);
		if (!ids.length) return;
		if (
			action.kind === "trash" &&
			!window.confirm(
				`${ids.length}건(${formatWon(
					receipts
						.filter((receipt) => ids.includes(receipt.receiptId))
						.reduce((sum, receipt) => sum + receipt.totalAmount, 0),
				)})을 휴지통으로 보낼까요? 30일 안에 복원할 수 있어요.`,
			)
		)
			return;
		setBulkBusy(true);
		const result = await onBulk(ids, action);
		setBulkBusy(false);
		setRefundNotice(
			result.failed
				? `${result.ok}건은 바꿨고 ${result.failed}건은 저장하지 못했어요. 남은 기록만 다시 시도해 주세요.`
				: action.kind === "trash"
					? `${result.ok}건을 휴지통으로 보냈어요. 휴지통에서 되돌릴 수 있어요.`
					: `${result.ok}건의 분류를 바꿨어요.`,
		);
		if (!result.failed) {
			setSelected(new Set());
			setSelecting(false);
		}
	};
	const fixing = fixingId
		? receipts.find((receipt) => receipt.receiptId === fixingId)
		: undefined;
	const trackCalendar = useMemo(
		() => (source: string) => analytics.track("calendar_view", { source }),
		[],
	);
	return (
		<>
			<AppHeader channel={channel} title="소비 내역" help="edit" />
			<main className="app-main records-page">
				<div className="page-intro">
					<p className="eyebrow">기억 타임라인</p>
					<h1>내 소비 내역</h1>
					<p>기록한 소비를 찾고, 산 물건과 사용 후 생각을 함께 남겨요.</p>
				</div>
				<label className="search-field">
					<span>⌕</span>
					<input
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder="매장·분류·산 물건·메모·태그 검색"
						aria-label="기록 검색"
					/>
				</label>
				{!showTrash ? (
					<>
						<PeriodFilter choice={period} onChange={setPeriod} />
						<div className="records-summary">
							<p>
								<span>
									{resolvedPeriod ? resolvedPeriod.label : "전체 기간"} ·{" "}
									{visible.length}건
								</span>
								<b className="amount">{formatWon(visibleTotal)}</b>
							</p>
							<div className="records-summary-actions">
								<button
									type="button"
									className="quiet-button"
									aria-pressed={view === "calendar"}
									onClick={() => setView(view === "list" ? "calendar" : "list")}
								>
									<Icon
										name={view === "list" ? "calendar" : "list"}
										size={18}
									/>
									{view === "list" ? "달력" : "목록"}
								</button>
								<button
									type="button"
									className="quiet-button"
									aria-pressed={selecting}
									onClick={() => {
										setSelecting(!selecting);
										setSelected(new Set());
									}}
								>
									{selecting ? "고르기 끝" : "고르기"}
								</button>
							</div>
						</div>
					</>
				) : null}
				<fieldset className="record-filters">
					<legend className="sr-only">기록 보기</legend>
					<button
						className={!showTrash ? "active" : ""}
						type="button"
						onClick={() => setShowTrash(false)}
					>
						전체 기록
					</button>
					<button
						className={showTrash ? "active" : ""}
						type="button"
						onClick={() => setShowTrash(true)}
					>
						휴지통
						{receipts.some((receipt) => receipt.deletedAt)
							? ` (${receipts.filter((receipt) => receipt.deletedAt).length})`
							: ""}
					</button>
				</fieldset>
				{!showTrash ? (
					<fieldset className="record-filters memory-filters">
						<legend className="sr-only">기억 단계</legend>
						{(
							[
								["all", "모든 장면"],
								[
									"reflect",
									reflectionIds.size
										? `돌아볼 기록 ${reflectionIds.size}`
										: "돌아볼 기록",
								],
								["repurchase", "다시 살 것"],
							] as const
						).map(([value, label]) => (
							<button
								className={filter === value ? "active" : ""}
								key={value}
								type="button"
								onClick={() => setFilter(value)}
							>
								{label}
							</button>
						))}
					</fieldset>
				) : null}
				{refundNotice ? (
					<output className="form-note">{refundNotice}</output>
				) : null}
				{view === "calendar" && !showTrash ? (
					<CalendarView
						receipts={receipts}
						household={household}
						onTrack={trackCalendar}
					/>
				) : visible.length === 0 ? (
					resolvedPeriod || needle ? (
						<EmptyState
							title={
								needle
									? `"${query.trim()}"로 찾은 기록이 없어요`
									: "이 기간에는 기록이 없어요"
							}
							description={
								resolvedPeriod
									? "기간을 넓혀 보세요."
									: "다른 단어로 찾아보세요."
							}
							action={resolvedPeriod ? "전체 기간 보기" : "검색 지우기"}
							onAction={() =>
								resolvedPeriod
									? setPeriod({ ...period, kind: "all" })
									: setQuery("")
							}
						/>
					) : (
						<EmptyState
							title={showTrash ? "휴지통이 비어 있어요" : "아직 기록이 없어요"}
							description={
								showTrash
									? "지운 기록은 30일 동안 여기서 되돌릴 수 있어요."
									: "결제 알림을 붙여 넣으면 첫 기록이 생겨요."
							}
							action={showTrash ? undefined : "결제 알림 붙여 넣기"}
							onAction={() => navigate("/app/capture")}
						/>
					)
				) : (
					<div className="record-list">
						{visible.map((receipt) => (
							<article
								className={`record-card memory-record ${receipt.isFavorite ? "is-favorite" : ""} ${selected.has(receipt.receiptId) ? "is-selected" : ""}`}
								key={receipt.receiptId}
							>
								{selecting && !showTrash ? (
									<label className="record-select">
										<input
											type="checkbox"
											checked={selected.has(receipt.receiptId)}
											onChange={() => toggleSelected(receipt.receiptId)}
										/>
										<span className="sr-only">
											{receipt.storeDisplay} 고르기
										</span>
									</label>
								) : null}
								<span className="recent-symbol">
									<SymbolMark
										symbolId={
											categories.find(
												(category) =>
													category.categoryId === receipt.categoryId,
											)?.symbolId ?? "store"
										}
									/>
								</span>
								<div className="record-main">
									<button
										type="button"
										className="store-link"
										onClick={() => setStoreKeyOpen(receipt.storeKey)}
									>
										{receipt.storeDisplay}
									</button>
									<span>
										{
											categories.find(
												(category) =>
													category.categoryId === receipt.categoryId,
											)?.nameKo
										}{" "}
										· {formatKstDateTime(receipt.transactedAt)}
									</span>
									{receipt.tagIds?.length ? (
										<div className="record-tags">
											{receipt.tagIds.map((id) => (
												<span key={id}>{tagLabel(tags, id)}</span>
											))}
										</div>
									) : null}
									{receipt.memo ? (
										<p className="memory-note">“{receipt.memo}”</p>
									) : null}
									{receipt.mood || receipt.occasion ? (
										<div className="memory-meta">
											{receipt.mood ? (
												<span>{MOOD_LABELS[receipt.mood]}</span>
											) : null}
											{receipt.occasion ? (
												<span>{OCCASION_LABELS[receipt.occasion]}</span>
											) : null}
										</div>
									) : null}
									{receipt.purchaseReason || receipt.satisfaction ? (
										<div className="memory-arc">
											{receipt.purchaseReason ? (
												<span>
													처음엔{" "}
													{PURCHASE_REASON_LABELS[receipt.purchaseReason]}
												</span>
											) : null}
											{receipt.satisfaction ? (
												<strong>
													{SATISFACTION_LABELS[receipt.satisfaction]}
												</strong>
											) : null}
										</div>
									) : null}
									{receipt.afterNote ? (
										<p className="after-note">써보니 · {receipt.afterNote}</p>
									) : null}
								</div>
								<b>{formatWon(receipt.totalAmount)}</b>
								{!receipt.deletedAt &&
									!receipt.sensitiveMasked &&
									isHomeItemCategory(receipt.categoryId) && (
										<ReceiptItemsEditor
											receipt={receipt}
											knownItems={[
												...household.items.map((item) => item.name),
												...household.shopping
													.filter((entry) => entry.status === "pending")
													.map((entry) => entry.name),
											]}
											onUpdate={onUpdate}
										/>
									)}
								{showTrash ? (
									<div className="record-actions-inline">
										{receipt.voidReason === "refund" ? (
											<p className="record-void-note">
												취소·환불로 되돌린 기록
											</p>
										) : null}
										<button
											className="delete-button restore"
											type="button"
											onClick={async () => {
												const restored = await onRestore(receipt.receiptId);
												setRefundNotice(
													restored
														? receipt.voidReason === "refund"
															? "환불 전 기록과 그때 다시 열린 장보기를 되돌렸어요."
															: "기록과 연결된 장보기를 복원했어요."
														: "복원을 저장하지 못했어요. 원본은 휴지통에 그대로 있어요. 다시 시도해 주세요.",
												);
											}}
										>
											{receipt.voidReason === "refund"
												? "환불 되돌리기"
												: "복원"}
										</button>
									</div>
								) : (
									<div className="record-actions-inline">
										<button
											className="memory-edit-button"
											type="button"
											onClick={() => setFixingId(receipt.receiptId)}
										>
											고치기
										</button>
										{!receipt.sensitiveMasked && (
											<button
												className="memory-edit-button"
												type="button"
												aria-expanded={editingId === receipt.receiptId}
												onClick={() =>
													setEditingId((current) =>
														current === receipt.receiptId
															? null
															: receipt.receiptId,
													)
												}
											>
												{editingId === receipt.receiptId
													? "닫기"
													: "기대와 실제"}
											</button>
										)}
										<button
											className="delete-button refund"
											type="button"
											onClick={async () => {
												const refunded = await onRefund(receipt.receiptId);
												setRefundNotice(
													refunded
														? "생활비 반영을 빼고, 이 결제가 완료한 장보기는 다시 열어뒀어요. 실제 재고 상태만 확인해 주세요."
														: "환불 처리를 저장하지 못했어요. 원본 기록과 장보기는 그대로예요. 다시 시도해 주세요.",
												);
											}}
										>
											결제 취소·환불
										</button>
										<button
											className="delete-button"
											type="button"
											aria-label={`${receipt.storeDisplay} 삭제`}
											onClick={() => void onDelete(receipt.receiptId)}
										>
											삭제
										</button>
									</div>
								)}
								{!showTrash &&
								editingId === receipt.receiptId &&
								!receipt.sensitiveMasked ? (
									<div className="memory-editor">
										<div className="memory-editor-heading">
											<p className="eyebrow">기대에서 실제로</p>
											<strong>다음 선택에 남길 한 가지를 골라요.</strong>
										</div>
										<label>
											처음 고른 이유
											<select
												value={receipt.purchaseReason ?? ""}
												onChange={(event) =>
													onUpdate(receipt.receiptId, {
														purchaseReason: (event.target.value ||
															null) as PurchaseReason | null,
													})
												}
											>
												<option value="">선택하지 않음</option>
												{Object.entries(PURCHASE_REASON_LABELS).map(
													([value, label]) => (
														<option key={value} value={value}>
															{label}
														</option>
													),
												)}
											</select>
										</label>
										<label>
											기대했던 장면
											<input
												defaultValue={receipt.expectedScene ?? ""}
												maxLength={160}
												onBlur={(event) =>
													onUpdate(receipt.receiptId, {
														expectedScene: event.target.value.trim() || null,
													})
												}
											/>
										</label>
										<fieldset className="satisfaction-picker">
											<legend>써보니 어땠나요?</legend>
											<div>
												{Object.entries(SATISFACTION_LABELS).map(
													([value, label]) => (
														<button
															className={
																receipt.satisfaction === value ? "active" : ""
															}
															key={value}
															type="button"
															onClick={() =>
																onUpdate(receipt.receiptId, {
																	satisfaction: value as PurchaseSatisfaction,
																	reviewedAt: new Date().toISOString(),
																})
															}
														>
															{label}
														</button>
													),
												)}
											</div>
										</fieldset>
										<fieldset className="repurchase-picker">
											<legend>다시 고른다면?</legend>
											<div>
												{Object.entries(REPURCHASE_LABELS).map(
													([value, label]) => (
														<button
															className={
																receipt.repurchaseIntent === value
																	? "active"
																	: ""
															}
															key={value}
															type="button"
															onClick={() =>
																onUpdate(receipt.receiptId, {
																	repurchaseIntent: value as RepurchaseIntent,
																	reviewedAt: new Date().toISOString(),
																})
															}
														>
															{label}
														</button>
													),
												)}
											</div>
										</fieldset>
										<label>
											써보고 남길 한 줄
											<textarea
												defaultValue={receipt.afterNote ?? ""}
												maxLength={240}
												onBlur={(event) =>
													onUpdate(receipt.receiptId, {
														afterNote: event.target.value.trim() || null,
														reviewedAt: event.target.value.trim()
															? new Date().toISOString()
															: (receipt.reviewedAt ?? null),
													})
												}
												placeholder="예: 생각보다 손이 자주 가고, 다음엔 작은 용량이면 충분하겠다"
											/>
										</label>
										{findComparableMemories(receipt, receipts).length ? (
											<div className="memory-comparables">
												<span>함께 떠올려 볼 장면</span>
												{findComparableMemories(receipt, receipts).map(
													(item) => (
														<button
															key={item.receiptId}
															type="button"
															onClick={() => setEditingId(item.receiptId)}
														>
															{item.storeDisplay} ·{" "}
															{formatKstDate(item.transactedAt)}
														</button>
													),
												)}
											</div>
										) : null}
										<label>
											장면의 한 줄
											<textarea
												key={`${receipt.receiptId}:${receipt.memo ?? ""}`}
												defaultValue={receipt.memo ?? ""}
												maxLength={200}
												onBlur={(event) =>
													onUpdate(receipt.receiptId, {
														memo: event.target.value.trim() || null,
													})
												}
											/>
										</label>
										<fieldset className="mood-picker compact">
											<legend className="sr-only">기분 선택</legend>
											{(
												Object.entries(MOOD_LABELS) as [MemoryMood, string][]
											).map(([value, label]) => (
												<button
													className={receipt.mood === value ? "active" : ""}
													key={value}
													type="button"
													onClick={() =>
														onUpdate(receipt.receiptId, {
															mood: receipt.mood === value ? null : value,
														})
													}
												>
													{label}
												</button>
											))}
										</fieldset>
										<button
											className={
												receipt.isFavorite
													? "favorite-button active"
													: "favorite-button"
											}
											type="button"
											aria-pressed={receipt.isFavorite ?? false}
											onClick={() =>
												onUpdate(receipt.receiptId, {
													isFavorite: !receipt.isFavorite,
												})
											}
										>
											{receipt.isFavorite
												? "★ 오래 기억할 장면"
												: "☆ 오래 기억할 장면으로 표시"}
										</button>
									</div>
								) : null}
							</article>
						))}
					</div>
				)}
			</main>
			{selecting && !showTrash ? (
				<div className="bulk-bar" role="toolbar" aria-label="고른 기록 정리">
					<span>{selected.size}건 고름</span>
					<button
						type="button"
						className="quiet-button"
						onClick={() =>
							setSelected(
								selected.size === visible.length
									? new Set()
									: new Set(
											visible.slice(0, 200).map((receipt) => receipt.receiptId),
										),
							)
						}
					>
						{selected.size === visible.length && visible.length
							? "모두 풀기"
							: resolvedPeriod
								? "이 기간 전부"
								: "보이는 것 전부"}
					</button>
					<select
						aria-label="바꿀 분류"
						value={bulkCategory}
						onChange={(event) => setBulkCategory(event.target.value)}
					>
						<option value="">분류 바꾸기…</option>
						{categories
							.filter((category) => !category.sensitive)
							.map((category) => (
								<option key={category.categoryId} value={category.categoryId}>
									{category.nameKo}
								</option>
							))}
					</select>
					<button
						type="button"
						className="button button-small"
						disabled={bulkBusy || !selected.size || !bulkCategory}
						onClick={() =>
							void runBulk({ kind: "category", categoryId: bulkCategory })
						}
					>
						바꾸기
					</button>
					<button
						type="button"
						className="quiet-button"
						disabled={bulkBusy || !selected.size}
						onClick={() => void runBulk({ kind: "trash" })}
					>
						휴지통으로
					</button>
				</div>
			) : (
				<AppNav active="records" />
			)}
			{fixing ? (
				<RecordEditor
					receipt={fixing}
					onClose={() => setFixingId(null)}
					onSave={(patch, remember) =>
						onEdit(fixing.receiptId, patch, remember)
					}
				/>
			) : null}
			{storeKeyOpen ? (
				<StoreSummary
					storeKey={storeKeyOpen}
					receipts={receipts}
					onClose={() => setStoreKeyOpen(null)}
					onRuleChange={(key, categoryId) => {
						const rules = readStoreRules();
						writeStoreRules(
							categoryId
								? setStoreRule(rules, key, categoryId)
								: removeStoreRule(rules, key),
						);
						analytics.track("store_rule_saved", {
							action: categoryId ? "set" : "removed",
						});
					}}
					onOpenRecord={(id) => {
						setStoreKeyOpen(null);
						setFixingId(id);
					}}
				/>
			) : null}
		</>
	);
}

function Report({
	household,
	closes,
	receipts,
	channel,
	commerce,
	entitlements,
	onEntitlements,
	bridge,
}: {
	household: HouseholdConsumptionState;
	closes: DayCloseRecord[];
	receipts: Receipt[];
	channel: Channel;
	commerce?: CommercePort | undefined;
	entitlements: CommerceEntitlement[];
	onEntitlements: (entitlements: CommerceEntitlement[]) => void;
	bridge?: ChannelBridge | undefined;
}) {
	const archiveOfferTitleId = useId();
	const [cardAmount, setCardAmount] = useState(false);
	const [cardNotice, setCardNotice] = useState("");
	const [, setStatementRevision] = useState(0);
	const [period, setPeriod] = useState<ReportPeriod>("month");
	const [monthStartDay] = useState(() =>
		Number(window.localStorage.getItem("sobidogam:monthStartDay") ?? "1"),
	);
	const [commerceNotice, setCommerceNotice] = useState("");
	const [commerceBusy, setCommerceBusy] = useState(false);
	const [archiveOfferRequested, setArchiveOfferRequested] = useState(false);
	const [lifetimePrice, setLifetimePrice] = useState("9,900원");
	const [annualPrice, setAnnualPrice] = useState("2,900원");
	const [annualAvailable, setAnnualAvailable] = useState(false);
	const anchor = new Date();
	const comparison = summarizeReports(receipts, period, anchor, monthStartDay);
	const tagWindow =
		period === "month"
			? resolvePeriod("this_month", anchor, monthStartDay)
			: period === "week"
				? { from: new Date(anchor.getTime() - 7 * 86_400_000), to: anchor }
				: {
						from: new Date(`${anchor.getFullYear()}-01-01T00:00:00+09:00`),
						to: new Date(`${anchor.getFullYear() + 1}-01-01T00:00:00+09:00`),
					};
	const summary = comparison.current;
	const previous = comparison.previous;
	const groupsUsed = groups
		.map((group) => ({
			group,
			count: Object.entries(summary.categoryCounts ?? {})
				.filter(
					([categoryId]) =>
						categories.find((category) => category.categoryId === categoryId)
							?.groupId === group.groupId,
				)
				.reduce((total, [, count]) => total + count, 0),
		}))
		.filter((item) => item.count > 0)
		.sort((a, b) => b.count - a.count);
	const periodLabel =
		period === "week" ? "주간" : period === "year" ? "연간" : "월간";
	const annual = summarizeReports(receipts, "year", anchor, monthStartDay);
	const memoryArchive = buildMemoryArchive(receipts, anchor.getFullYear());
	const deltaWon = comparison.delta.totalAmount;
	const memoryAsset = receipts.filter((receipt) => !receipt.deletedAt);
	const enrichedCount = memoryAsset.filter(
		(receipt) =>
			receipt.memo || receipt.mood || receipt.occasion || receipt.isFavorite,
	).length;
	const reportFavoriteCount = memoryAsset.filter(
		(receipt) => receipt.isFavorite,
	).length;
	const hasLifetime = entitlements.some(
		(entitlement) => entitlement.sku === "lifetime" && !entitlement.revokedAt,
	);
	const hasAnnualReport = entitlements.some(
		(entitlement) =>
			entitlement.sku === "annual_report" &&
			!entitlement.revokedAt &&
			entitlement.periodKey === String(anchor.getFullYear()),
	);
	const canExportAnnual = hasLifetime || hasAnnualReport;
	const archiveOfferDecision = decideArchiveOffer({
		hasArchiveEntitlement: canExportAnnual,
		firstValueCompleted: memoryAsset.length > 0,
		explicitArchiveRequest: archiveOfferRequested,
		initialFreeCohortComplete: commerce?.initialFreeCohortComplete === true,
		commercialReleaseApproved: commerce?.commercialReleaseApproved === true,
		paymentAvailable: commerce?.available === true,
	});
	const canRequestArchiveOffer =
		!canExportAnnual &&
		memoryAsset.length > 0 &&
		commerce?.available === true &&
		commerce.initialFreeCohortComplete &&
		commerce.commercialReleaseApproved;
	useEffect(() => {
		if (!archiveOfferDecision.renderPaywall || !commerce) return;
		analytics.track("paywall_view", { trigger: "annual", channel });
		void commerce
			.listProducts()
			.then((products) => {
				const lifetime = products.find((product) => product.sku === "lifetime");
				const annualReport = products.find(
					(product) => product.sku === "annual_report",
				);
				if (lifetime) setLifetimePrice(lifetime.displayAmount);
				if (annualReport) {
					setAnnualPrice(annualReport.displayAmount);
					setAnnualAvailable(true);
				}
			})
			.catch(() => setCommerceNotice("상품 정보를 불러오지 못했어요."));
	}, [archiveOfferDecision.renderPaywall, channel, commerce]);
	const purchaseArchive = async (sku: "lifetime" | "annual_report") => {
		if (!commerce || !archiveOfferDecision.renderPaywall) return;
		setCommerceBusy(true);
		setCommerceNotice("");
		analytics.track("paywall_cta", { sku, channel });
		analytics.track("purchase_start", { sku, channel });
		try {
			const result = await commerce.purchase(sku);
			if (result.ok) {
				onEntitlements([
					...entitlements.filter((item) => item.sku !== result.entitlement.sku),
					result.entitlement,
				]);
				setCommerceNotice(
					sku === "lifetime"
						? "기억 보관함 평생권을 열었어요."
						: `${anchor.getFullYear()}년 기억책을 열었어요.`,
				);
				analytics.track("purchase_success", { sku, channel });
			} else {
				setCommerceNotice(
					result.code === "cancelled"
						? "결제를 취소했어요. 언제든 다시 선택할 수 있어요."
						: "결제는 완료하지 않았어요. 청구 여부를 확인한 뒤 복원을 시도해 주세요.",
				);
				analytics.track("purchase_failed", {
					sku,
					channel,
					code: result.code,
				});
			}
		} finally {
			setCommerceBusy(false);
		}
	};
	const downloadAnnualPdf = () => {
		const pdf = buildAnnualReportPdf(
			annual.current,
			annual.previous,
			memoryArchive,
		);
		downloadBlob(
			new Blob([pdf.buffer as ArrayBuffer], { type: "application/pdf" }),
			`sobidogam-${annual.current.period}-annual.pdf`,
		);
	};
	const restorePurchases = async () => {
		if (!commerce) return;
		setCommerceBusy(true);
		try {
			const restored = await commerce.restore();
			onEntitlements(restored);
			setCommerceNotice(
				restored.some((item) => !item.revokedAt)
					? "구매 내역을 복원했어요."
					: "복원할 구매 내역이 없어요.",
			);
		} catch {
			setCommerceNotice(
				"구매 내역을 확인하지 못했어요. 잠시 후 다시 시도해 주세요.",
			);
		} finally {
			setCommerceBusy(false);
		}
	};
	return (
		<>
			<AppHeader
				channel={channel}
				title={`${periodLabel} 리포트`}
				help="statement"
			/>
			<main className="app-main report-page">
				<WeeklyCloseSummary
					receipts={receipts}
					closes={closes}
					household={household}
				/>
				<MonthCloseCard receipts={receipts} household={household} />
				<div className="page-intro">
					<p className="eyebrow">{periodLabel} 기록 보고서</p>
					<h1>
						{period === "month"
							? `${formatKstDate(tagWindow.from).replace(/ \(.*\)$/, "")} ~ ${formatKstDate(new Date(tagWindow.to.getTime() - 1)).replace(/ \(.*\)$/, "")} 돌아보기`
							: `${summary.period} 기록 돌아보기`}
						{period === "month" &&
						statementMatched(
							resolvePeriod("this_month", anchor, monthStartDay)
								.from.toISOString()
								.slice(0, 10),
							summary.totalAmount ?? 0,
						) ? (
							<span className="matched-badge">명세와 맞춰 봄</span>
						) : null}
					</h1>
					<p>지난 기간의 표본을 숫자보다 장면으로 돌아봐요.</p>
				</div>
				<fieldset className="period-tabs">
					<legend className="sr-only">리포트 기간</legend>
					{(
						[
							["week", "주간"],
							["month", "월간"],
							["year", "연말"],
						] as const
					).map(([value, label]) => (
						<button
							className={period === value ? "active" : ""}
							key={value}
							type="button"
							onClick={() => setPeriod(value)}
						>
							{label}
						</button>
					))}
				</fieldset>
				<div className="report-hero">
					<span className="eyebrow">열린 칸</span>
					<strong>
						{summary.unlockedCount}
						<small> / 128칸</small>
					</strong>
					<div className="progress-track">
						<span
							style={{
								width: `${Math.min(100, (summary.unlockedCount / 128) * 100)}%`,
							}}
						/>
					</div>
				</div>
				<section className="memory-asset-summary" aria-label="내 기억 자산">
					<div>
						<span className="eyebrow">남긴 장면</span>
						<strong>{enrichedCount}</strong>
						<small>장면이 있는 표본</small>
					</div>
					<div>
						<strong>{reportFavoriteCount}</strong>
						<small>오래 기억할 장면</small>
					</div>
					<p>
						금액이 아니라 내가 남긴 문장과 다시 찾은 장소가 기억 자산이 됩니다.
					</p>
				</section>
				<section className="comparison-card" aria-label="지난 기간 비교">
					<div>
						<span className="eyebrow">비교</span>
						<strong>
							{comparison.delta.recordCount >= 0 ? "+" : ""}
							{comparison.delta.recordCount}건
						</strong>
						<small>기록 수</small>
					</div>
					<div>
						<strong>
							{comparison.delta.unlockedCount >= 0 ? "+" : ""}
							{comparison.delta.unlockedCount}칸
						</strong>
						<small>새로 열린 칸</small>
					</div>
					<div>
						<strong>
							{deltaWon >= 0 ? "+" : ""}
							{formatWon(Math.abs(deltaWon))}
						</strong>
						<small>기록한 총액 차이</small>
					</div>
					<p>
						이전 기간 {previous.period} · 기록 {previous.recordCount ?? 0}건
					</p>
				</section>
				<section className="report-card">
					<p className="eyebrow">자주 찾은 곳</p>
					{groupsUsed.length ? (
						groupsUsed.slice(0, 3).map(({ group, count }) => (
							<div className="bar-row" key={group.groupId}>
								<span>{group.nameKo}</span>
								<div>
									<span
										style={{
											width: `${Math.min(100, count * 18)}%`,
											background: `var(${group.colorToken})`,
										}}
									/>
								</div>
								<b>{count}건</b>
							</div>
						))
					) : (
						<EmptyState
							title="아직 표본이 없어요"
							description="첫 기록이 이 카드의 시작이 됩니다."
							action="기록 시작"
							onAction={() => navigate("/app/capture")}
						/>
					)}
				</section>
				<CategoryChart
					current={summary.categoryAmounts}
					previous={previous.categoryAmounts}
					previousLabel={
						period === "week" ? "지난주" : period === "year" ? "작년" : "지난달"
					}
				/>
				<TagTotals
					receipts={receipts}
					tags={readTags()}
					from={tagWindow.from}
					to={tagWindow.to}
				/>
				{period === "month" ? (
					<>
						<a
							className="text-link"
							href="/app/records?period=this_month"
							onClick={(event) => {
								event.preventDefault();
								navigate("/app/records?period=this_month");
							}}
						>
							이 기간 내역 보기 →
						</a>
						<StatementCheck
							receipts={receipts}
							onChange={() => setStatementRevision((version) => version + 1)}
							onTrack={(name, props) => analytics.track(name, props)}
						/>
					</>
				) : null}
				{period === "year" ? (
					<AnnualPreview
						receipts={receipts}
						onPdf={() =>
							canExportAnnual
								? downloadAnnualPdf()
								: navigate("/app/plus?from=report")
						}
					/>
				) : null}
				{canExportAnnual ? (
					<section
						className="memory-archive"
						aria-label={`${memoryArchive.year}년 기억 보관함`}
					>
						<p className="eyebrow">{memoryArchive.year}년 기억 보관함</p>
						<h2>한 해의 소비를 장면으로 다시 봐요.</h2>
						<div className="memory-archive-stats">
							<div>
								<strong>{memoryArchive.enrichedCount}</strong>
								<small>기억을 더한 장면</small>
							</div>
							<div>
								<strong>{memoryArchive.revisitedPlaceCount}</strong>
								<small>다시 찾은 장소</small>
							</div>
							<div>
								<strong>{memoryArchive.mostActiveSeason ?? "—"}</strong>
								<small>가장 많이 움직인 계절</small>
							</div>
							<div>
								<strong>{memoryArchive.mostActiveWeekday ?? "—"}</strong>
								<small>가장 많이 기록한 요일</small>
							</div>
						</div>
						{memoryArchive.topRevisitedPlaces.length ? (
							<div className="memory-archive-revisits">
								<h3>다시 찾은 장소</h3>
								{memoryArchive.topRevisitedPlaces.map((place) => (
									<p key={place.storeDisplay}>
										<span>{place.storeDisplay}</span>
										<strong>{place.visits}번</strong>
									</p>
								))}
							</div>
						) : null}
						<div className="memory-archive-moments">
							<h3>오래 남은 문장</h3>
							{memoryArchive.moments.length ? (
								memoryArchive.moments.map((moment) => (
									<article key={moment.receiptId}>
										<small>
											{moment.date} · {moment.storeDisplay}
										</small>
										<p>“{moment.memo}”</p>
										{moment.mood ? (
											<span>{MOOD_LABELS[moment.mood]}</span>
										) : null}
									</article>
								))
							) : (
								<p className="memory-archive-empty">
									타임라인에서 한 줄을 더하면 여기에 모여요.
								</p>
							)}
						</div>
					</section>
				) : null}
				{canRequestArchiveOffer && !archiveOfferRequested ? (
					<section className="archive-discovery" aria-label="기억책 옵션">
						<p>
							기본 리포트와 내보내기는 계속 무료예요. 원할 때만 연간 기억책 소장
							옵션을 확인할 수 있어요.
						</p>
						<button
							className="quiet-button full-width"
							type="button"
							onClick={() => setArchiveOfferRequested(true)}
						>
							기억책 옵션 살펴보기
						</button>
					</section>
				) : null}
				{!canExportAnnual &&
				memoryAsset.length > 0 &&
				!canRequestArchiveOffer ? (
					<section className="archive-discovery" aria-label="기억책 옵션">
						<p>
							소장용 결산 PDF와 깊은 회고는 한 번 구매 상품으로 준비 중이에요.
							기본 리포트와 올해 결산 미리 보기는 계속 무료예요.
						</p>
						<a
							className="quiet-button full-width"
							href="/app/plus"
							onClick={(event) => {
								event.preventDefault();
								navigate("/app/plus?from=report");
							}}
						>
							무료·유료 범위 보기
						</a>
					</section>
				) : null}
				{archiveOfferDecision.renderPaywall ? (
					<section
						className="archive-offer"
						aria-labelledby={archiveOfferTitleId}
					>
						<p className="eyebrow">기억 보관함 · 한 번 구매</p>
						<h2 id={archiveOfferTitleId}>기억 보관함 평생권</h2>
						<p>
							매달 내는 구독 없이, 연간 기억 아카이브와 계절·재방문 심화 회고를
							계속 보관해요. 기록·타임라인·내보내기는 구매하지 않아도 그대로 쓸
							수 있어요.
						</p>
						<ul>
							<li>연도별 기억 아카이브와 소장용 결산 PDF</li>
							<li>계절·요일·재방문 장소 심화 회고</li>
							<li>오래 남긴 문장을 한곳에서 다시 보기</li>
						</ul>
						<p className="field-hint">
							디지털 콘텐츠라 결제 후 열람하면 청약철회가 제한될 수 있어요.
							구독이 아니라 해지할 것이 없어요.
						</p>
						<button
							className="button full-width"
							type="button"
							disabled={commerceBusy}
							onClick={() => void purchaseArchive("lifetime")}
						>
							평생권 {lifetimePrice}에 열기 <span>→</span>
						</button>
						{annualAvailable && !hasAnnualReport ? (
							<button
								className="archive-secondary-button full-width"
								type="button"
								disabled={commerceBusy}
								onClick={() => void purchaseArchive("annual_report")}
							>
								{anchor.getFullYear()}년 기억책만 {annualPrice}
							</button>
						) : null}
						<button
							className="quiet-button full-width"
							type="button"
							disabled={commerceBusy}
							onClick={() => void restorePurchases()}
						>
							이전에 구매했다면 복원
						</button>
						<button
							className="quiet-button full-width"
							type="button"
							disabled={commerceBusy}
							onClick={() => setArchiveOfferRequested(false)}
						>
							지금은 무료 리포트 계속 보기
						</button>
						{commerceNotice ? (
							<output className="commerce-notice">{commerceNotice}</output>
						) : null}
					</section>
				) : null}
				<div className="report-actions">
					{canExportAnnual ? (
						<button
							className="button full-width"
							type="button"
							onClick={() => {
								const pdf = buildAnnualReportPdf(
									annual.current,
									annual.previous,
									memoryArchive,
								);
								downloadBlob(
									new Blob([pdf.buffer as ArrayBuffer], {
										type: "application/pdf",
									}),
									`sobidogam-${annual.current.period}-annual.pdf`,
								);
							}}
						>
							연말 결산 PDF로 저장 <span>→</span>
						</button>
					) : null}
					<label className="unlock-amount-toggle">
						<input
							type="checkbox"
							checked={cardAmount}
							onChange={(event) => setCardAmount(event.target.checked)}
						/>
						카드에 금액 보이기
					</label>
					<button
						className="quiet-button"
						type="button"
						onClick={async () => {
							const blob = await shareCardBlob(
								{
									period: summary.period,
									unlockedCount: summary.unlockedCount,
									totalCells: 128,
									streakDays: summary.streakDays,
									topCategoryIds: summary.topCategoryIds,
									...(summary.recordCount !== undefined
										? { recordCount: summary.recordCount }
										: {}),
									...(summary.totalAmount !== undefined
										? { totalAmount: summary.totalAmount }
										: {}),
								},
								{ showAmount: cardAmount },
							);
							const outcome = await shareCard({
								blob,
								filename: `sobidogam-${summary.period}-share.png`,
								text: `${summary.period} 소비 기록 카드`,
								webUrl:
									"https://sobidogam.com/dogam?utm_source=share&utm_medium=card",
								tossPath: "/app/report",
								channel,
								bridge,
								download: downloadBlob,
							});
							reportCardShare("report", outcome, channel);
							setCardNotice(cardShareNotice(outcome));
						}}
					>
						카드 보내기
					</button>
					{cardNotice ? (
						<output className="form-note">{cardNotice}</output>
					) : null}
				</div>
			</main>
			<AppNav active="report" />
		</>
	);
}

function Settings({
	dayCloses,
	dayCloseRecovery,
	receipts,
	household,
	onClear,
	onReplace,
	channel,
	apiBaseUrl,
	identity,
	webPushPublicKey,
	requestPushPermission,
	onRemoveTag,
}: {
	onRemoveTag: (tagId: string) => Promise<boolean>;
	dayCloses: DayCloseRecord[];
	dayCloseRecovery?: DayCloseRecovery | undefined;
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	onClear: () => Promise<boolean>;
	onReplace: (backup: {
		receipts: Receipt[];
		household: HouseholdConsumptionState;
		dayCloses?: DayCloseRecord[];
	}) => Promise<boolean>;
	channel: Channel;
	apiBaseUrl?: string | undefined;
	identity?: IdentityPort | undefined;
	webPushPublicKey?: string | undefined;
	requestPushPermission?: (() => Promise<boolean>) | undefined;
}) {
	const [monthStartDay, setMonthStartDay] = useState(
		window.localStorage.getItem("sobidogam:monthStartDay") ?? "1",
	);
	const [soundMuted, setSoundMuted] = useState(isJuiceMuted);
	const [stats, setStats] = useState(
		window.localStorage.getItem("sobidogam:stats") !== "false",
	);
	const [pushEnabled, setPushEnabled] = useState(
		window.localStorage.getItem("sobidogam:pushEnabled") === "true",
	);
	const [installOpen, setInstallOpen] = useState(false);
	const [quietStart, setQuietStart] = useState(
		window.localStorage.getItem("sobidogam:pushQuietStart") ?? "22:00",
	);
	const [quietEnd, setQuietEnd] = useState(
		window.localStorage.getItem("sobidogam:pushQuietEnd") ?? "08:00",
	);
	const [notice, setNotice] = useState("");
	const [backupCode, setBackupCode] = useState("");
	const [backupAcknowledged, setBackupAcknowledged] = useState(false);
	const [restoreCode, setRestoreCode] = useState("");
	const [restoreFile, setRestoreFile] = useState<File | null>(null);
	const [backupBusy, setBackupBusy] = useState(false);
	const [recoveryCopies, setRecoveryCopies] = useState(
		safeListLocalDataQuarantines,
	);
	const serverFeatures = useServerFeatures(apiBaseUrl);
	const [tags, setTags] = useState<TagDef[]>(readTags);
	const [rules, setRules] = useState(readStoreRules);
	useEffect(() => {
		const id = window.location.hash.slice(1);
		if (/^[a-z-]+$/.test(id))
			document
				.querySelector(`[data-anchor="${id}"]`)
				?.scrollIntoView({ block: "start" });
	}, []);
	const tagUsage = receipts.reduce<Record<string, number>>(
		(counts, receipt) => {
			if (receipt.deletedAt) return counts;
			for (const id of receipt.tagIds ?? []) counts[id] = (counts[id] ?? 0) + 1;
			return counts;
		},
		{},
	);
	const exportData = () => {
		if (dayCloseRecovery) return;
		downloadBlob(
			new Blob(
				[
					JSON.stringify(
						{ schemaVersion: 2, receipts, household, dayCloses },
						null,
						2,
					),
				],
				{ type: "application/json" },
			),
			"sobidogam-export.json",
		);
		setNotice("소비 기록과 집 안 물건을 함께 백업했어요.");
		analytics.track("export_run", {
			format: "json",
			recordCount: receipts.length,
			householdItemCount: household.items.length,
		});
	};
	const exportCsvData = () => {
		downloadBlob(
			new Blob([exportCsv(receipts)], { type: "text/csv;charset=utf-8" }),
			"sobidogam-export.csv",
		);
		setNotice("다른 가계부로 옮길 기록 파일을 준비했어요.");
		analytics.track("export_run", {
			format: "csv",
			recordCount: receipts.length,
		});
	};
	const createBackup = async () => {
		if (dayCloseRecovery) return;
		setBackupBusy(true);
		try {
			const { code, envelope } = await encryptBackup(
				receipts,
				household,
				dayCloses,
			);
			let uploaded = false;
			if (apiBaseUrl && serverFeatures.serverBackup) {
				try {
					const response = await fetch(
						`${apiBaseUrl.replace(/\/$/, "")}/api/v1/backup`,
						{
							method: "PUT",
							headers: {
								"Content-Type": "application/json",
								...(await installHeaders(channel, identity)),
							},
							body: JSON.stringify({
								codeHash: await recoveryCodeHash(code),
								...envelope,
							}),
						},
					);
					uploaded = response.ok;
				} catch {
					uploaded = false;
				}
			}
			downloadBlob(
				new Blob([JSON.stringify(envelope, null, 2)], {
					type: "application/json",
				}),
				"sobidogam-backup.json",
			);
			setBackupCode(code);
			analytics.track("backup_put", {
				sizeBytes: envelope.ciphertextB64.length,
			});
			setNotice(
				uploaded
					? "암호화 백업을 서버와 파일에 저장했어요. 복구코드는 따로 보관하세요."
					: "백업 파일을 내려받았어요. 파일과 복구코드를 함께 보관하세요.",
			);
		} catch {
			setNotice("백업을 만들지 못했어요. 잠시 후 다시 시도해 주세요.");
		} finally {
			setBackupBusy(false);
		}
	};
	const changePushPreference = async (enabled: boolean) => {
		if (!enabled) {
			setPushEnabled(false);
			window.localStorage.setItem("sobidogam:pushEnabled", "false");
			analytics.track("push_denied", { channel, reason: "disabled" });
			if (apiBaseUrl) {
				try {
					await fetch(
						`${apiBaseUrl.replace(/\/$/, "")}/api/v1/push/subscription`,
						{
							method: "DELETE",
							headers: await installHeaders(channel, identity),
						},
					);
				} catch {
					// Local preference remains authoritative when the network is offline.
				}
			}
			setNotice("알림을 껐어요.");
			return;
		}
		if (channel === "ait") {
			const agreed = await requestPushPermission?.().catch(() => false);
			if (!agreed) {
				setPushEnabled(false);
				window.localStorage.setItem("sobidogam:pushEnabled", "false");
				analytics.track("push_denied", { channel, reason: "unavailable" });
				setNotice("토스 알림 동의를 완료하지 않아 알림을 켜지 않았어요.");
				return;
			}
		} else if (typeof Notification !== "undefined") {
			const permission = await Notification.requestPermission();
			if (permission !== "granted") {
				setPushEnabled(false);
				window.localStorage.setItem("sobidogam:pushEnabled", "false");
				analytics.track("push_denied", { channel, reason: permission });
				setNotice("알림 권한을 허용하지 않아 알림을 켜지 않았어요.");
				return;
			}
			if (!apiBaseUrl || !webPushPublicKey || !("serviceWorker" in navigator)) {
				setPushEnabled(false);
				window.localStorage.setItem("sobidogam:pushEnabled", "false");
				setNotice("온라인 알림이 아직 준비되지 않아 알림을 켜지 않았어요.");
				return;
			}
			try {
				const registration = await navigator.serviceWorker.ready;
				const applicationServerKey = new Uint8Array(
					fromBase64Url(webPushPublicKey),
				).buffer;
				const subscription = await registration.pushManager.subscribe({
					userVisibleOnly: true,
					applicationServerKey,
				});
				const serialized = subscription.toJSON();
				const response = await fetch(
					`${apiBaseUrl.replace(/\/$/, "")}/api/v1/push/subscription`,
					{
						method: "PUT",
						headers: {
							"Content-Type": "application/json",
							...(await installHeaders(channel, identity)),
						},
						body: JSON.stringify({
							endpoint: serialized.endpoint,
							p256dh: serialized.keys?.p256dh,
							auth: serialized.keys?.auth,
							weekly: true,
							streakRisk: true,
							quietStart,
							quietEnd,
							timeZone: "Asia/Seoul",
						}),
					},
				);
				if (!response.ok) throw new Error("push registration rejected");
			} catch {
				setPushEnabled(false);
				window.localStorage.setItem("sobidogam:pushEnabled", "false");
				setNotice("알림 설정을 저장하지 못했어요. 잠시 후 다시 시도해 주세요.");
				return;
			}
		} else {
			setNotice("이 브라우저에서는 알림을 받을 수 없어요.");
			return;
		}
		setPushEnabled(true);
		window.localStorage.setItem("sobidogam:pushEnabled", "true");
		window.localStorage.setItem("sobidogam:pushQuietStart", quietStart);
		window.localStorage.setItem("sobidogam:pushQuietEnd", quietEnd);
		analytics.track("push_subscribe", { channel });
		setNotice("알림을 켰어요. 조용한 시간에는 알림을 보내지 않아요.");
	};
	const restore = async () => {
		if (!restoreCode && !restoreFile) {
			setNotice("복구코드를 입력해 주세요.");
			return;
		}
		setBackupBusy(true);
		try {
			let sourceFile = restoreFile;
			if (!sourceFile && apiBaseUrl && serverFeatures.serverBackup) {
				const response = await fetch(
					`${apiBaseUrl.replace(/\/$/, "")}/api/v1/backup/fetch`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							...(await installHeaders(channel, identity)),
						},
						body: JSON.stringify({
							codeHash: await recoveryCodeHash(restoreCode),
						}),
					},
				);
				if (response.ok) {
					const body = (await response.json()) as {
						data?: {
							schemaVer: number;
							saltB64: string;
							ciphertextB64: string;
						};
					};
					if (body.data)
						sourceFile = new File(
							[JSON.stringify(body.data)],
							"cloud-backup.json",
							{
								type: "application/json",
							},
						);
				}
			}
			if (!sourceFile) throw new Error("E-BACKUP-404");
			const restored = await decryptBackup(sourceFile, restoreCode);
			if (!(await onReplace(restored))) throw new Error("E-LOCAL-RESTORE");
			analytics.track("backup_restore", { ok: true });
			setRestoreCode("");
			setRestoreFile(null);
			setNotice(
				`${restored.receipts.length}건의 기록과 집 안 물건 ${restored.household.items.length}개를 복원했어요.`,
			);
		} catch (error) {
			analytics.track("backup_restore", { ok: false });
			setNotice(
				error instanceof Error && error.message === "E-LOCAL-RESTORE"
					? "복원하지 못했어요. 기존 기록과 복구 입력은 유지했으니 저장 공간을 확인하고 다시 시도해 주세요."
					: "복구코드가 맞지 않거나 백업 파일이 손상됐어요.",
			);
		} finally {
			setBackupBusy(false);
		}
	};
	const removeRecoveryCopy = (copy: LocalDataQuarantine) => {
		if (
			!window.confirm(
				"보관한 원문을 지울까요? 내려받지 않았다면 다시 되찾을 수 없어요.",
			)
		)
			return;
		try {
			deleteLocalDataQuarantine(window.localStorage, copy.storageKey);
			setRecoveryCopies(safeListLocalDataQuarantines());
			setNotice("보관한 복구 원문을 지웠어요.");
		} catch {
			setNotice("복구 원문을 지우지 못했어요. 잠시 후 다시 시도해 주세요.");
		}
	};
	return (
		<>
			<AppHeader channel={channel} title="설정" />
			<main className="app-main settings-page">
				<label className="check-row">
					<input
						type="checkbox"
						checked={!soundMuted}
						onChange={(e) => {
							setSoundMuted(!e.target.checked);
							setJuiceMuted(!e.target.checked);
						}}
					/>
					효과음
				</label>
				<div className="page-intro">
					<p className="eyebrow">내 기록 관리</p>
					<h1>
						기록을 <em>돌봐요.</em>
					</h1>
				</div>
				{notice && <output className="notice notice-sage">{notice}</output>}
				{recoveryCopies.length > 0 && (
					<section className="settings-group recovery-vault">
						<h2>복구 보관함</h2>
						<p>
							손상된 저장본을 비우기 전에 복사한 원문이에요. 확인한 뒤 직접
							내려받거나 지울 수 있어요.
						</p>
						{recoveryCopies.map((copy) => (
							<div key={copy.storageKey} className="recovery-vault-row">
								<QuarantineDownloads copy={copy} />
								<button
									className="quiet-button"
									type="button"
									onClick={() => removeRecoveryCopy(copy)}
								>
									이 보관본 지우기
								</button>
							</div>
						))}
					</section>
				)}
				<section className="settings-group">
					<h2>기록 방식</h2>
					<label className="setting-row" data-anchor="month-start">
						<span>
							<strong>월 시작일</strong>
							<small>급여일에 맞춰 한 달을 세어요.</small>
						</span>
						<select
							value={monthStartDay}
							onChange={(event) => {
								setMonthStartDay(event.target.value);
								window.localStorage.setItem(
									"sobidogam:monthStartDay",
									event.target.value,
								);
							}}
						>
							{Array.from({ length: 28 }, (_, index) => (
								<option key={`month-${index + 1}`} value={index + 1}>
									{index + 1}일
								</option>
							))}
						</select>
					</label>
					<label className="setting-row">
						<span>
							<strong>사용 통계 보내기</strong>
							<small>개인 기록·금액·매장명은 보내지 않아요.</small>
						</span>
						<input
							type="checkbox"
							checked={stats}
							onChange={(event) => {
								setStats(event.target.checked);
								analytics.setEnabled(event.target.checked);
								window.localStorage.setItem(
									"sobidogam:stats",
									String(event.target.checked),
								);
							}}
						/>
					</label>
					{channel === "ait" ? (
						<>
							<label className="setting-row">
								<span>
									<strong>주간·연속 기록 알림</strong>
									<small>직접 켠 경우에만 하루 한 번 알려 드려요.</small>
								</span>
								<input
									type="checkbox"
									checked={pushEnabled}
									onChange={(event) =>
										void changePushPreference(event.target.checked)
									}
								/>
							</label>
							<div className="quiet-hours">
								<span>
									<strong>조용한 시간</strong>
									<small>이 시간에는 기록 알림을 보내지 않아요.</small>
								</span>
								<div>
									<label className="quiet-time-picker">
										<span className="sr-only">조용한 시간 시작</span>
										<input
											type="time"
											value={quietStart}
											onChange={(event) => {
												setQuietStart(event.target.value);
												window.localStorage.setItem(
													"sobidogam:pushQuietStart",
													event.target.value,
												);
											}}
										/>
										<span className="quiet-time-value" aria-hidden="true">
											{quietStart}
										</span>
									</label>
									<span aria-hidden="true">~</span>
									<label className="quiet-time-picker">
										<span className="sr-only">조용한 시간 종료</span>
										<input
											type="time"
											value={quietEnd}
											onChange={(event) => {
												setQuietEnd(event.target.value);
												window.localStorage.setItem(
													"sobidogam:pushQuietEnd",
													event.target.value,
												);
											}}
										/>
										<span className="quiet-time-value" aria-hidden="true">
											{quietEnd}
										</span>
									</label>
								</div>
							</div>
						</>
					) : (
						<div className="calendar-reminders" data-anchor="reminders">
							<span>
								<strong>내 달력에 알림 넣기</strong>
								<small>
									월 시작일({monthStartDay}일) 저녁 지난달 돌아보기와 일요일
									저녁 기록 알림을 휴대폰 달력에 넣어요. 금액·매장은 들어가지
									않아요.
								</small>
							</span>
							<button
								className="button button-small"
								type="button"
								onClick={() => {
									downloadBlob(
										icsBlob([
											monthCloseReminder(Number(monthStartDay) || 1),
											weeklyReminder(),
										]),
										"sobidogam-reminders.ics",
									);
									analytics.track("ics_downloaded", { kind: "reminders" });
									setNotice(
										"달력 파일을 내려받았어요. 열면 달력에 추가돼요. 아이폰에서 안 열리면 파일 앱에서 열어 주세요.",
									);
								}}
							>
								달력에 넣기
							</button>
							<span>
								<strong>홈 화면에 두기</strong>
								<small>결제 알림이 뜨면 아이콘에서 바로 붙여 넣어요.</small>
							</span>
							<button
								className="button button-small"
								type="button"
								onClick={() => setInstallOpen(true)}
							>
								홈 화면에 추가
							</button>
							{installOpen ? (
								<InstallGuide
									onClose={() => setInstallOpen(false)}
									onOutcome={(outcome, platform) =>
										analytics.track("install_prompt", { outcome, platform })
									}
								/>
							) : null}
						</div>
					)}
				</section>
				<section className="settings-group">
					<h2>분류와 태그</h2>
					<h3 className="settings-subhead" data-anchor="rules">
						매장 규칙
					</h3>
					{Object.keys(rules).length === 0 ? (
						<p className="field-hint">분류를 한 번 바꾸면 여기 모여요.</p>
					) : (
						<ul className="rule-list">
							{Object.entries(rules).map(([storeKey, categoryId]) => (
								<li key={storeKey}>
									<span>
										{receipts.find((receipt) => receipt.storeKey === storeKey)
											?.storeDisplay ?? storeKey}
										<small>
											{categories.find((item) => item.categoryId === categoryId)
												?.nameKo ?? "기타"}
										</small>
									</span>
									<button
										type="button"
										className="quiet-button"
										onClick={() => {
											const next = removeStoreRule(rules, storeKey);
											setRules(next);
											writeStoreRules(next);
											analytics.track("store_rule_saved", {
												action: "removed",
											});
											setNotice(
												"규칙을 지웠어요. 다음부터는 매장 이름으로 추정해요.",
											);
										}}
									>
										지우기
									</button>
								</li>
							))}
						</ul>
					)}
					<h3 className="settings-subhead">태그</h3>
					<TagManager
						tags={tags}
						usage={tagUsage}
						onChange={(next, removedId) => {
							setTags(next);
							writeTags(next);
							if (removedId)
								void onRemoveTag(removedId).then((ok) =>
									setNotice(
										ok
											? "태그를 지웠어요. 기록은 그대로 남아 있어요."
											: "태그 연결을 정리하지 못했어요. 다시 시도해 주세요.",
									),
								);
						}}
					/>
				</section>
				<section className="settings-group">
					<h2>내 기록 관리</h2>
					{dayCloseRecovery ? (
						<p>
							마감을 읽지 못해 전체 백업은 잠시 멈췄어요. 소비 기록은 CSV로 받을
							수 있고, 아래에서 정상 백업을 복원할 수 있어요.
						</p>
					) : null}
					{listDayCloseRecoveryCopies(window.localStorage).map((copy) => (
						<button
							className="quiet-button"
							type="button"
							key={copy.storageKey}
							onClick={() =>
								downloadLocalRaw(
									copy.raw,
									`sobidogam-day-closes-${copy.createdAt.replace(/[^0-9]/g, "")}.txt`,
								)
							}
						>
							복원 전 마감 원문 받기 ·{" "}
							{new Date(copy.createdAt).toLocaleString("ko-KR")}
						</button>
					))}
					<button
						className="setting-action"
						type="button"
						onClick={() => navigate("/app/records?select=1")}
					>
						<span>☐</span>
						<span>
							<strong>기간 골라 정리</strong>
							<small>
								여러 기록의 분류를 바꾸거나 한 번에 휴지통으로 보내요.
							</small>
						</span>
						<b>→</b>
					</button>
					<button
						className="setting-action"
						type="button"
						disabled={Boolean(dayCloseRecovery)}
						onClick={exportData}
					>
						<span>↓</span>
						<span>
							<strong>전체 기록 백업</strong>
							<small>
								소비 기록·집 안 물건·쓸 돈·마감을 한 파일로 보관해요.
							</small>
						</span>
						<b>→</b>
					</button>
					<button
						className="setting-action"
						type="button"
						disabled={
							backupBusy || !backupAcknowledged || Boolean(dayCloseRecovery)
						}
						onClick={() => void createBackup()}
					>
						<span>⌁</span>
						<span>
							<strong>복구코드 백업</strong>
							<small>기록을 암호화해 파일로 보관해요.</small>
						</span>
						<b>→</b>
					</button>
					<label className="backup-ack">
						<input
							type="checkbox"
							checked={backupAcknowledged}
							onChange={(event) => setBackupAcknowledged(event.target.checked)}
						/>
						<span>
							백업을 만들면 표시되는 복구코드를 따로 보관할게요. 코드를
							잃어버리면 백업을 되찾을 수 없어요.
						</span>
					</label>
					{backupCode && (
						<output className="backup-code-card">
							<strong>복구코드</strong>
							<code>{backupCode}</code>
							<small>
								이 코드는 다시 보여 드리지 않아요. 안전한 곳에 적어 두세요.
							</small>
						</output>
					)}
					<div className="restore-form">
						<label className="setting-row restore-file-picker">
							<span className="setting-copy">
								<strong>백업 파일 복원</strong>
								<small>
									{serverFeatures.serverBackup
										? "선택하지 않으면 서버 백업을 찾아요."
										: "전체 기록 JSON 또는 복구코드 백업 파일을 골라 주세요."}
								</small>
							</span>
							<span className="restore-file-control">
								<input
									type="file"
									accept="application/json"
									onChange={(event) =>
										setRestoreFile(event.target.files?.[0] ?? null)
									}
								/>
								<span>{restoreFile ? "파일 선택됨" : "파일 선택"}</span>
							</span>
						</label>
						<label>
							복구코드 (암호화 백업만)
							<input
								value={restoreCode}
								onChange={(event) => setRestoreCode(event.target.value)}
								placeholder="예: 5자리-5자리-5자리"
								inputMode="text"
							/>
						</label>
						<button
							className="quiet-button"
							type="button"
							disabled={
								backupBusy || (!restoreFile && !serverFeatures.serverBackup)
							}
							onClick={() => void restore()}
						>
							백업 복원하기
						</button>
					</div>
					<button
						className="setting-action"
						type="button"
						onClick={exportCsvData}
					>
						<span>↓</span>
						<span>
							<strong>다른 가계부로 옮기기</strong>
							<small>다른 가계부에서 읽을 수 있도록 기록을 내려받아요.</small>
						</span>
						<b>→</b>
					</button>
				</section>
				<section className="settings-group">
					<h2>도움과 플러스</h2>
					<button
						className="setting-action"
						type="button"
						onClick={() => navigate("/app/help")}
					>
						<span>?</span>
						<span>
							<strong>도움말</strong>
							<small>자주 묻는 질문 20개에서 찾아요.</small>
						</span>
						<b>→</b>
					</button>
					<IssueReportButton
						channel={channel}
						screen="설정"
						className="setting-action"
						label={
							<>
								<span>!</span>
								<span>
									<strong>문제 알리기</strong>
									<small>앱 버전·화면만 붙여 메일로 알려요.</small>
								</span>
								<b>→</b>
							</>
						}
						onOpen={() =>
							analytics.track("issue_report_open", { from: "settings" })
						}
					/>
					<button
						className="setting-action"
						type="button"
						onClick={() => navigate("/app/plus")}
					>
						<span>+</span>
						<span>
							<strong>소비도감 플러스</strong>
							<small>무료·유료 범위, 내 구매, 환불·복원</small>
						</span>
						<b>→</b>
					</button>
				</section>
				<section className="settings-group danger-group">
					<h2>삭제</h2>
					<button
						className="setting-action"
						type="button"
						onClick={async () => {
							if (
								!window.confirm(
									"기기의 모든 기록을 지울까요? 내보낸 파일은 남아 있어요.",
								)
							)
								return;
							if (
								window.prompt("확인하려면 '전체 삭제'를 입력하세요.") !==
								"전체 삭제"
							) {
								setNotice("삭제를 취소했어요.");
								return;
							}
							const serverDeleted = await onClear();
							setNotice(
								serverDeleted
									? "기기와 서버의 소비도감 데이터를 모두 삭제했어요."
									: "기기 데이터는 삭제했지만 서버 삭제 확인은 오프라인이라 남아 있어요. 온라인에서 다시 시도해 주세요.",
							);
						}}
					>
						<span>×</span>
						<span>
							<strong>전체 삭제</strong>
							<small>삭제한 기록은 되돌릴 수 없어요.</small>
						</span>
						<b>→</b>
					</button>
				</section>
				<p className="settings-links">
					<a
						href="/privacy"
						onClick={(event) => {
							event.preventDefault();
							navigate("/privacy");
						}}
					>
						개인정보 처리방침
					</a>
					<a
						href="/terms"
						onClick={(event) => {
							event.preventDefault();
							navigate("/terms");
						}}
					>
						이용약관
					</a>
					<a
						href="/support"
						onClick={(event) => {
							event.preventDefault();
							navigate("/support");
						}}
					>
						문의하기
					</a>
				</p>
			</main>
			<AppNav active="settings" />
		</>
	);
}

function AppNav({ active }: { active: string }) {
	const items = [
		{ id: "home", label: "오늘", path: "/app" },
		{ id: "records", label: "내역", path: "/app/records" },
		{ id: "dogam", label: "도감", path: "/app/dogam" },
		{ id: "household", label: "살림", path: "/app/household" },
		{ id: "report", label: "정리", path: "/app/report" },
	] as const;
	return (
		<nav className="app-nav" aria-label="앱 메뉴">
			{items.map((item) => (
				<a
					className={active === item.id ? "active" : ""}
					href={item.path}
					aria-current={active === item.id ? "page" : undefined}
					key={item.id}
					onClick={(event) => {
						event.preventDefault();
						navigate(item.path);
					}}
				>
					<span className="nav-icon">
						<Icon name={item.id} />
					</span>
					{item.label}
				</a>
			))}
		</nav>
	);
}

function EmptyState({
	title,
	description,
	action,
	onAction,
}: {
	title: string;
	description: string;
	action?: string | undefined;
	onAction?: (() => void) | undefined;
}) {
	return (
		<div className="empty-state">
			<span>○</span>
			<h3>{title}</h3>
			<p>{description}</p>
			{action && onAction && (
				<button className="text-link" type="button" onClick={onAction}>
					{action} →
				</button>
			)}
		</div>
	);
}

/** One short line under "도감에 넣었어요": the budget consequence of the save. */
function saveDetail(
	records: Receipt[],
	receipts: Receipt[],
	household: HouseholdConsumptionState,
): string | undefined {
	const record = records[0];
	if (!record || records.length > 1) return undefined;
	const plan = resolveLivingBudgetPlan(
		household,
		new Date(),
		monthStartDayPref(),
	);
	const limit = calculateCategoryLimits(
		receipts.filter((receipt) => !receipt.deletedAt),
		activeCategoryLimits(household, plan),
		monthStartDayPref(),
	).find((item) => item.categoryId === record.categoryId);
	if (limit) {
		const name =
			categories.find((category) => category.categoryId === limit.categoryId)
				?.nameKo ?? "이 분류";
		return limit.remaining >= 0
			? `${name} 한도 ${formatWon(limit.remaining)} 남았어요`
			: `이번 달 ${name} 한도를 ${formatWon(-limit.remaining)} 넘었어요`;
	}
	if (!hasLivingBudget(household, plan)) return undefined;
	const budget = calculateLivingBudget(
		receipts.filter((receipt) => !receipt.deletedAt),
		plan,
	);
	const remaining = budget.totalLimit - budget.totalSpent;
	return remaining >= 0
		? `이번 달 생활비 ${formatWon(remaining)} 남았어요`
		: `이번 달 생활비를 ${formatWon(-remaining)} 넘었어요`;
}

function ReadyProductApp({
	channel,
	apiBaseUrl,
	commerce,
	identity,
	webPushPublicKey,
	requestPushPermission,
	bridge,
	initialLocalData,
}: ProductUiProps & {
	initialLocalData: Extract<LocalDataLoadResult, { status: "ready" }>;
}) {
	const [path, setPath] = useState(
		window.location.pathname.replace(/\/+$/, "") || "/",
	);
	const [receipts, setReceipts] = useState<Receipt[]>(
		initialLocalData.receipts,
	);
	const [household, setHousehold] = useState<HouseholdConsumptionState>(
		initialLocalData.household,
	);
	const receiptsRef = useRef(receipts);
	const householdRef = useRef(household);
	const receiptHouseholdQueue = useRef(createSerialAsyncQueue());
	const [drafts, setDrafts] = useState<Draft[]>([]);
	const [draftNotice, setDraftNotice] = useState<string | undefined>();
	const [entitlements, setEntitlements] = useState<CommerceEntitlement[]>([]);
	const [dayCloses, setDayCloses] = useState<DayCloseRecord[]>([]);
	const dayClosesRef = useRef<DayCloseRecord[]>([]);
	const [dayCloseRecovery, setDayCloseRecovery] =
		useState<DayCloseRecovery | null>(null);
	const [closingDate, setClosingDate] = useState<string | null>(null);
	const [closeResult, setCloseResult] = useState<"saved" | "error" | null>(
		null,
	);
	const quickCloseBusy = useRef(false);
	const [allowanceOpen, setAllowanceOpen] = useState(false);
	const [recordBusy, setRecordBusy] = useState(false);
	const [recordError, setRecordError] = useState("");
	const recordAttempt = useRef(0);
	const [saveToast, setSaveToast] = useState<SaveToastState | null>(null);
	const saveUndoEvidence = useRef<{
		ids: string[];
		before: HouseholdConsumptionState;
		after: HouseholdConsumptionState;
	} | null>(null);
	const [unlock, setUnlock] = useState<UnlockState | null>(null);
	const [installOffer, setInstallOffer] = useState(false);
	const receiptStore = useMemo(() => createReceiptStore(), []);
	useEffect(installJuiceUnlock, []);
	const [initialization, setInitialization] = useState<
		"pending" | "ready" | "failed"
	>("pending");
	useEffect(() => {
		receiptsRef.current = receipts;
	}, [receipts]);
	useEffect(() => {
		householdRef.current = household;
	}, [household]);
	const analyticsPort = useMemo(
		() =>
			createAnalyticsPort({
				enabled: window.localStorage.getItem("sobidogam:stats") !== "false",
				...(apiBaseUrl ? { apiBaseUrl } : {}),
				storage: window.localStorage,
				identity:
					identity ?? createInstallIdentity(window.localStorage, channel),
				clientVersion: "0.3.0",
			}),
		[apiBaseUrl, channel, identity],
	);
	analytics = analyticsPort;
	useEffect(() => {
		const flush = () => void analyticsPort.flush();
		const timer = window.setInterval(flush, 30_000);
		window.addEventListener("online", flush);
		window.addEventListener("pagehide", flush);
		flush();
		return () => {
			window.clearInterval(timer);
			window.removeEventListener("online", flush);
			window.removeEventListener("pagehide", flush);
		};
	}, [analyticsPort]);
	useEffect(() => {
		if (!commerce?.available) return;
		void commerce
			.listEntitlements()
			.then(setEntitlements)
			.catch(() => setEntitlements([]));
	}, [commerce]);
	useEffect(() => {
		let cancelled = false;
		void receiptHouseholdQueue.current
			.enqueue(async () => {
				if (cancelled) return;
				const closeLoad = await loadDayCloses(receiptStore);
				const stored = await receiptStore.listAllReceipts();
				if (cancelled) return;
				const restored = (stored.length ? stored : receiptsRef.current).map(
					normalizeReceiptForStorage,
				);
				let reconciled = householdRef.current;
				for (const receipt of restored.filter((receipt) => !receipt.deletedAt))
					reconciled = reconcileReceiptItems(reconciled, receipt);
				await persistReceiptAndHouseholdChange({
					storage: window.localStorage,
					receiptStore,
					beforeReceipts: stored,
					afterReceipts: restored,
					beforeHousehold: householdRef.current,
					afterHousehold: reconciled,
					changedReceipts: restored,
				});
				if (cancelled) return;
				receiptsRef.current = restored;
				householdRef.current = reconciled;
				setReceipts(restored);
				setHousehold(reconciled);
				if (closeLoad.status === "ready") {
					dayClosesRef.current = closeLoad.closes;
					setDayCloses(closeLoad.closes);
				}
				setDayCloseRecovery(closeLoad.status === "recovery" ? closeLoad : null);
				setInitialization("ready");
			})
			.catch(() => {
				rememberError("E-LOCAL-OPEN");
				if (!cancelled) setInitialization("failed");
			});
		return () => {
			cancelled = true;
		};
	}, [receiptStore]);
	useEffect(() => {
		if (initialization !== "ready" || dayCloseRecovery) return;
		const summary = firstWeekSummary(
			receiptsRef.current,
			new Date(),
			dayClosesRef.current,
		);
		if (summary) analytics.track("first_week_summary", { channel, ...summary });
	}, [initialization, channel, dayCloseRecovery]);
	useEffect(() => {
		const onPop = () =>
			setPath(window.location.pathname.replace(/\/+$/, "") || "/");
		window.addEventListener("popstate", onPop);
		const isFirstEver =
			window.localStorage.getItem("sobidogam:session-seen") !== "true";
		window.localStorage.setItem("sobidogam:session-seen", "true");
		analytics.track(
			path.startsWith("/app") ? "session_start" : "landing_view",
			path.startsWith("/app")
				? { channel, isFirstEver, daysSinceFirst: daysSinceFirstBucket() }
				: { channel, path, ...landingSource() },
		);
		if (path === "/pricing")
			analytics.track("pricing_view", { channel, from: "public" });
		if (path === "/app/plus")
			analytics.track("pricing_view", {
				channel,
				from:
					new URLSearchParams(window.location.search).get("from") === "report"
						? "report"
						: "settings",
			});
		// 진입 이벤트는 즉시 보낸다.
		//
		// 실측 2026-08-12: 첫 방문 계측 요청이 0건이었다. 원인은 코드 부재가 아니라 **순서**다 —
		// mount 시 flush 하는 effect가 이 effect보다 먼저 선언돼 있어 `landing_view`가 큐에
		// 들어가기 **전에** 비워지고, 다음 flush는 30초 뒤다. `track()`의 자동 flush 임계도
		// 큐 20건이라 첫 방문(1건)은 어느 쪽으로도 나가지 않는다.
		// 이탈률이 가장 높은 첫 수 초 cohort가 통째로 빠지므로 여기서 한 번 밀어낸다.
		void analytics.flush();
		return () => window.removeEventListener("popstate", onPop);
	}, [channel, path]);
	const saveDrafts = (nextDrafts: Draft[], notice?: string) => {
		setDrafts(
			nextDrafts.map((next) => {
				const { key, display } = normalizeStoreName(next.storeDisplay);
				const safe = applySensitivityPolicy(
					{
						storeKey: key,
						storeDisplay: display,
						items: next.items,
						totalAmount: next.totalAmount,
						transactedAt: next.transactedAt,
						categoryId: next.categoryId,
						memo: next.memo ?? null,
					},
					next.categoryId,
					categories,
				);
				return {
					...next,
					storeDisplay: safe.storeDisplay,
					categoryId: safe.categoryId,
					notificationPreview: categories.find(
						(c) => c.categoryId === safe.categoryId,
					)?.sensitive
						? undefined
						: next.notificationPreview,
				};
			}),
		);
		setDraftNotice(notice);
		navigate("/app/confirm");
	};
	const recordFromClipboard = async () => {
		if (recordBusy) return;
		recordAttempt.current += 1;
		setRecordBusy(true);
		setRecordError("");
		markCaptureStarted();
		try {
			const text = bridge?.readClipboard
				? await bridge.readClipboard()
				: await navigator.clipboard.readText();
			if (!text?.trim()) throw new Error("empty");
			const parsed = parsePaymentNotificationText(text);
			if (!parsed.records.length) {
				navigate("/app/capture");
				setRecordError(
					"결제 알림을 찾지 못했어요. 붙여 넣기 칸에서 확인해 주세요.",
				);
				return;
			}
			const next = parsed.records.map((r) => {
				const normalized = normalizeStoreName(r.storeDisplay);
				const inferred = classifyWithRules({
					storeKey: normalized.key,
					aiCategoryHint: null,
					catalog,
					rules: readStoreRules(),
				});
				return {
					...r,
					categoryId: inferred.categoryId,
					items: [],
					warnings: r.warnings,
					notificationPreview: parsed.records.length === 1 ? text : undefined,
				} satisfies Draft;
			});
			setPendingCaptureMode("notification");
			saveDrafts(next);
		} catch {
			navigate("/app/capture");
			setRecordError(
				"클립보드를 읽을 수 없어요. 붙여 넣기 칸을 길게 눌러 직접 붙여 주세요.",
			);
		} finally {
			setRecordBusy(false);
		}
	};
	const saveClose = (
		close: DayCloseRecord | null,
		date: string,
	): Promise<boolean> =>
		receiptHouseholdQueue.current.enqueue(async () => {
			if (dayCloseRecovery) return false;
			const next = close
				? upsertDayClose(dayClosesRef.current, close)
				: dayClosesRef.current.filter((c) => c.date !== date);
			if (JSON.stringify(next) === JSON.stringify(dayClosesRef.current))
				return true;
			try {
				await writeDayCloses(receiptStore, next);
			} catch {
				return false;
			}
			dayClosesRef.current = next;
			setDayCloses(next);
			analytics.track("daily_close", {
				kind: close?.kind ?? "close_only",
				homeItem: Boolean(close?.homeItemIds.length),
				late: date < kstDateKey(new Date()),
				undo: close === null,
			});
			if (close) playJuice("stamp.collect", bridge?.haptic);
			return true;
		});
	const openDayClose = async (date: string, quick = false) => {
		if (quickCloseBusy.current) return;
		setCloseResult(null);
		if (quick && !dayClosesRef.current.some((close) => close.date === date)) {
			quickCloseBusy.current = true;
			try {
				const plan = resolveLivingBudgetPlan(
					householdRef.current,
					dateAnchor(date),
					monthStartDayPref(),
				);
				const daily = calculateDailyAllowance(
					receiptsRef.current,
					plan,
					householdRef.current.recurringPayments,
					dateAnchor(date),
				);
				const ok = await saveClose(
					{
						date,
						spent: daily.spent,
						allowance: daily.allowance,
						kind: deriveDayKind(daily.spent, daily.allowance),
						homeItemIds: [],
					},
					date,
				);
				setCloseResult(ok ? "saved" : "error");
			} finally {
				quickCloseBusy.current = false;
			}
		}
		setClosingDate(date);
	};
	const persist = (nextDrafts: Draft[]): Promise<boolean> =>
		receiptHouseholdQueue.current.enqueue(async () => {
			if (
				!nextDrafts.length ||
				nextDrafts.some(
					(draft) =>
						!draft.storeDisplay.trim() ||
						!Number.isSafeInteger(draft.totalAmount) ||
						draft.totalAmount <= 0 ||
						!Number.isFinite(Date.parse(draft.transactedAt)),
				)
			)
				return false;
			const currentReceipts = receiptsRef.current;
			const currentHousehold = householdRef.current;
			const savedRecordAttempt = recordAttempt.current;
			if (
				nextDrafts.some(
					(draft) =>
						draft.recurringPaymentId &&
						recurringPaymentRecorded(
							draft.recurringPaymentId,
							currentReceipts,
							new Date(draft.transactedAt),
						),
				)
			)
				return false;
			const now = new Date().toISOString();
			const records = nextDrafts.map((next) => {
				const normalized = normalizeStoreName(next.storeDisplay);
				const safe = applySensitivityPolicy(
					{
						storeKey: normalized.key,
						storeDisplay: normalized.display,
						items: next.items,
						totalAmount: next.totalAmount,
						transactedAt: next.transactedAt,
						categoryId: next.categoryId,
						memo: next.memo ?? null,
					},
					next.categoryId,
					categories,
				);
				return normalizeReceiptForStorage({
					receiptId: crypto.randomUUID(),
					transactedAt: next.transactedAt,
					storeKey: safe.storeKey,
					storeDisplay: safe.storeDisplay,
					brandId: classify({
						storeKey: safe.storeKey,
						aiCategoryHint: safe.categoryId,
						catalog,
					}).brandId,
					categoryId: safe.categoryId,
					totalAmount: Math.max(0, Math.round(next.totalAmount)),
					currency: "KRW",
					source: next.source,
					categoryPinned: true,
					sensitiveMasked:
						categories.find(
							(category) => category.categoryId === safe.categoryId,
						)?.sensitive ?? false,
					memo: safe.memo ?? null,
					mood: categories.find(
						(category) => category.categoryId === safe.categoryId,
					)?.sensitive
						? null
						: (next.mood ?? null),
					occasion: categories.find(
						(category) => category.categoryId === safe.categoryId,
					)?.sensitive
						? null
						: (next.occasion ?? null),
					isFavorite: categories.find(
						(category) => category.categoryId === safe.categoryId,
					)?.sensitive
						? false
						: (next.isFavorite ?? false),
					items: safe.items,
					purchaseReason: categories.find(
						(category) => category.categoryId === safe.categoryId,
					)?.sensitive
						? null
						: (next.purchaseReason ?? null),
					expectedScene: categories.find(
						(category) => category.categoryId === safe.categoryId,
					)?.sensitive
						? null
						: next.expectedScene?.trim() || null,
					createdAt: now,
					updatedAt: now,
					deletedAt: null,
					syncState: "local",
					tagIds: next.tagIds ?? [],
					recurringPaymentId: next.recurringPaymentId ?? null,
				} satisfies Receipt);
			});
			const updated = [...records, ...currentReceipts];
			let updatedHousehold = currentHousehold;
			for (const record of records)
				updatedHousehold = reconcileReceiptItems(updatedHousehold, record);
			const previousCategories = new Set(
				currentReceipts
					.filter((receipt) => !receipt.deletedAt)
					.map((receipt) => receipt.categoryId),
			);
			const newCategories = records
				.map((record) => record.categoryId)
				.filter((categoryId) => !previousCategories.has(categoryId));
			try {
				await persistReceiptAndHouseholdChange({
					storage: window.localStorage,
					receiptStore,
					beforeReceipts: currentReceipts,
					afterReceipts: updated,
					beforeHousehold: currentHousehold,
					afterHousehold: updatedHousehold,
					changedReceipt: records[0]!,
					changedReceipts: records,
				});
			} catch {
				return false;
			}
			saveUndoEvidence.current = {
				ids: records.map((record) => record.receiptId),
				before: currentHousehold,
				after: updatedHousehold,
			};
			if (recordAttempt.current === savedRecordAttempt)
				setRecordError((current) => (current === recordError ? "" : current));
			receiptsRef.current = updated;
			householdRef.current = updatedHousehold;
			setReceipts(updated);
			setHousehold(updatedHousehold);
			if (newCategories[0]) {
				setUnlock({
					categoryId: newCategories[0],
					amount: records.find(
						(record) => record.categoryId === newCategories[0],
					)?.totalAmount,
					count: new Set(
						updated
							.filter((receipt) => !receipt.deletedAt)
							.map((receipt) => receipt.categoryId),
					).size,
				});
				analytics.track("dogam_unlock", {
					kind: "category",
					stage: 1,
					unlockedTotal: new Set(
						updated
							.filter((receipt) => !receipt.deletedAt)
							.map((receipt) => receipt.categoryId),
					).size,
				});
			}
			for (const record of records)
				analytics.track("receipt_saved", {
					source: record.source,
					sensitiveMasked: record.sensitiveMasked,
				});
			analytics.track("payment_trace_saved", {
				source: records[0]?.source ?? "manual",
				recordCount: records.length,
			});
			const firstValue = firstRecordSignal(
				pendingCaptureMode ??
					({
						ai: "photo",
						screenshot: "photo",
						manual: "manual",
						notification: "notification",
						csv: "csv",
					}[records[0]?.source ?? "manual"] ||
						"manual"),
			);
			pendingCaptureMode = null;
			if (firstValue) {
				if (
					!resolveLivingBudgetPlan(
						updatedHousehold,
						new Date(),
						monthStartDayPref(),
					).monthlySpendable &&
					window.localStorage.getItem("sobidogam:allowanceAsked") !== "true"
				) {
					window.localStorage.setItem("sobidogam:allowanceAsked", "true");
					setAllowanceOpen(true);
				}
				analytics.track("first_record_saved", { channel, ...firstValue });
				void analytics.flush();
				if (channel !== "ait" && shouldOfferInstall()) setInstallOffer(true);
			}
			playJuice("stamp.collect", bridge?.haptic);
			setDrafts([]);
			setDraftNotice(undefined);
			setSaveToast({
				key: records.map((record) => record.receiptId).join(","),
				receiptIds: records.map((record) => record.receiptId),
				message:
					records.length > 1
						? `${records.length}건을 도감에 넣었어요`
						: "도감에 넣었어요",
				detail: saveDetail(records, updated, updatedHousehold),
				changes: (() => {
					const now = new Date();
					const beforePlan = resolveLivingBudgetPlan(
						currentHousehold,
						now,
						monthStartDayPref(),
					);
					const afterPlan = resolveLivingBudgetPlan(
						updatedHousehold,
						now,
						monthStartDayPref(),
					);
					const before = calculateDailyAllowance(
						currentReceipts,
						beforePlan,
						currentHousehold.recurringPayments,
						now,
					);
					const after = calculateDailyAllowance(
						updated,
						afterPlan,
						updatedHousehold.recurringPayments,
						now,
					);
					const changedItems = updatedHousehold.items.filter(
						(item) =>
							currentHousehold.items.find((old) => old.itemId === item.itemId)
								?.status !== item.status,
					);
					const completed = updatedHousehold.shopping.filter(
						(e) =>
							e.purchasedReceiptId &&
							records.some((r) => r.receiptId === e.purchasedReceiptId),
					);
					return [
						before.remaining !== null && after.remaining !== null
							? `오늘 남은 돈 ${formatWon(before.remaining)} → ${formatWon(after.remaining)}`
							: "오늘 남은 돈 · 쓸 돈을 정하면 보여요",
						changedItems.length
							? `집 안 물건 ${changedItems.map((i) => i.name).join(" · ")} 있어요`
							: "집 안 물건 그대로",
						completed.length
							? `장보기 ${completed.map((e) => e.name).join(" · ")} 구매 완료`
							: "장보기 그대로",
					];
				})(),
				newCell: newCategories.length
					? `${new Set(updated.filter((r) => !r.deletedAt).map((r) => r.categoryId)).size}번째로 모은 칸(128칸 중)`
					: undefined,
			});
			navigate("/app");
			return true;
		});
	/** #23: remove what was just saved, as if it never happened. */
	const undoSave = (ids: string[]): Promise<boolean> =>
		receiptHouseholdQueue.current.enqueue(async () => {
			const beforeReceipts = receiptsRef.current;
			const removed = beforeReceipts.filter((receipt) =>
				ids.includes(receipt.receiptId),
			);
			if (!removed.length) return false;
			const afterReceipts = beforeReceipts.filter(
				(receipt) => !ids.includes(receipt.receiptId),
			);
			let afterHousehold = householdRef.current;
			const evidence = saveUndoEvidence.current;
			for (const receipt of removed) {
				afterHousehold = reverseReceiptItems(afterHousehold, receipt);
				afterHousehold = {
					...afterHousehold,
					items: afterHousehold.items.filter(
						(item) =>
							!(
								item.itemId.startsWith(`receipt:${receipt.receiptId}:`) &&
								item.purchaseCount === 0 &&
								evidence &&
								!evidence.before.items.some(
									(old) => old.itemId === item.itemId,
								) &&
								JSON.stringify(
									householdRef.current.items.find(
										(now) => now.itemId === item.itemId,
									),
								) ===
									JSON.stringify(
										evidence.after.items.find(
											(saved) => saved.itemId === item.itemId,
										),
									)
							),
					),
				};
			}
			if (
				evidence &&
				evidence.ids.length === ids.length &&
				evidence.ids.every((id) => ids.includes(id))
			) {
				afterHousehold = undoSavedReceiptStock(
					householdRef.current,
					afterHousehold,
					evidence.before,
					evidence.after,
					ids,
				);
			}
			try {
				await persistReceiptAndHouseholdChange({
					storage: window.localStorage,
					receiptStore,
					beforeReceipts,
					afterReceipts,
					beforeHousehold: householdRef.current,
					afterHousehold,
					replaceAll: true,
				});
			} catch {
				return false;
			}
			saveUndoEvidence.current = null;
			receiptsRef.current = afterReceipts;
			householdRef.current = afterHousehold;
			setReceipts(afterReceipts);
			setHousehold(afterHousehold);
			setUnlock(null);
			analytics.track("save_undone", { recordCount: removed.length });
			return true;
		});
	const persistReceiptHousehold = (
		buildChange: (
			receipts: Receipt[],
			household: HouseholdConsumptionState,
		) =>
			| {
					afterReceipts: Receipt[];
					afterHousehold: HouseholdConsumptionState;
					changedReceipt: Receipt;
			  }
			| undefined,
	): Promise<boolean> =>
		receiptHouseholdQueue.current.enqueue(async () => {
			const beforeReceipts = receiptsRef.current;
			const beforeHousehold = householdRef.current;
			const change = buildChange(beforeReceipts, beforeHousehold);
			if (!change) return false;
			try {
				await persistReceiptAndHouseholdChange({
					storage: window.localStorage,
					receiptStore,
					beforeReceipts,
					afterReceipts: change.afterReceipts,
					beforeHousehold,
					afterHousehold: change.afterHousehold,
					changedReceipt: change.changedReceipt,
				});
				receiptsRef.current = change.afterReceipts;
				householdRef.current = change.afterHousehold;
				setReceipts(change.afterReceipts);
				setHousehold(change.afterHousehold);
				return true;
			} catch {
				return false;
			}
		});
	const voidReceipt = async (
		id: string,
		voidReason: "refund" | null,
	): Promise<boolean> => {
		return persistReceiptHousehold((currentReceipts, currentHousehold) => {
			const now = new Date().toISOString();
			const original = currentReceipts.find(
				(receipt) => receipt.receiptId === id && !receipt.deletedAt,
			);
			if (!original) return undefined;
			const afterReceipts = currentReceipts.map((receipt) =>
				receipt.receiptId === id
					? {
							...receipt,
							deletedAt: now,
							voidReason,
							updatedAt: now,
							syncState: "local" as const,
						}
					: receipt,
			);
			const changedReceipt = afterReceipts.find(
				(receipt) => receipt.receiptId === id,
			);
			if (!changedReceipt) return undefined;
			return {
				afterReceipts,
				afterHousehold: reverseReceiptItems(currentHousehold, original),
				changedReceipt,
			};
		});
	};
	const deleteReceipt = (id: string) => voidReceipt(id, null);
	const refundReceipt = (id: string) => voidReceipt(id, "refund");
	const restoreReceipt = async (id: string): Promise<boolean> => {
		return persistReceiptHousehold((currentReceipts, currentHousehold) => {
			const original = currentReceipts.find(
				(receipt) => receipt.receiptId === id,
			);
			if (!original?.deletedAt) return undefined;
			const now = new Date().toISOString();
			const afterReceipts = currentReceipts.map((receipt) =>
				receipt.receiptId === id
					? { ...receipt, deletedAt: null, voidReason: null, updatedAt: now }
					: receipt,
			);
			const changedReceipt = afterReceipts.find(
				(receipt) => receipt.receiptId === id,
			);
			if (!changedReceipt) return undefined;
			return {
				afterReceipts,
				afterHousehold: restoreReceiptItems(currentHousehold, changedReceipt),
				changedReceipt,
			};
		});
	};
	const updateReceipt = (
		id: string,
		patch: Partial<Receipt>,
	): Promise<boolean> => {
		return persistReceiptHousehold((currentReceipts, currentHousehold) => {
			const now = new Date().toISOString();
			const afterReceipts = currentReceipts.map((receipt) =>
				receipt.receiptId === id
					? normalizeReceiptForStorage({
							...receipt,
							...patch,
							updatedAt: now,
							syncState: "local" as const,
						})
					: receipt,
			);
			const changedReceipt = afterReceipts.find(
				(receipt) => receipt.receiptId === id,
			);
			if (!changedReceipt) return undefined;
			return {
				afterReceipts,
				afterHousehold: reconcileReceiptItems(currentHousehold, changedReceipt),
				changedReceipt,
			};
		}).then((saved) => {
			if (!saved) return false;
			const changed = receiptsRef.current.find(
				(receipt) => receipt.receiptId === id,
			);
			analytics.track("memory_enriched", {
				hasMemo: Boolean(changed?.memo),
				hasMood: Boolean(changed?.mood),
				favorite: Boolean(changed?.isFavorite),
				hasReason: Boolean(changed?.purchaseReason || changed?.expectedScene),
				hasReflection: Boolean(
					changed?.satisfaction ||
						changed?.repurchaseIntent ||
						changed?.afterNote,
				),
			});
			return true;
		});
	};
	/** #21: fix a saved record; #22: optionally remember the store's category. */
	const editReceipt = (
		id: string,
		patch: RecordEdit,
		rememberRule: boolean,
	): Promise<boolean> =>
		persistReceiptHousehold((currentReceipts, currentHousehold) => {
			const original = currentReceipts.find(
				(receipt) => receipt.receiptId === id && !receipt.deletedAt,
			);
			if (
				!original ||
				!patch.storeDisplay.trim() ||
				!Number.isSafeInteger(patch.totalAmount) ||
				patch.totalAmount <= 0 ||
				!Number.isFinite(Date.parse(patch.transactedAt))
			)
				return undefined;
			const normalized = normalizeStoreName(
				original.sensitiveMasked ? original.storeDisplay : patch.storeDisplay,
			);
			const safe = applySensitivityPolicy(
				{
					storeKey: normalized.key,
					storeDisplay: normalized.display,
					items: original.items ?? [],
					totalAmount: patch.totalAmount,
					transactedAt: patch.transactedAt,
					categoryId: patch.categoryId,
					memo: original.memo,
				},
				patch.categoryId,
				categories,
			);
			const changedReceipt = normalizeReceiptForStorage({
				...original,
				storeKey: safe.storeKey,
				storeDisplay: safe.storeDisplay,
				categoryId: safe.categoryId,
				categoryPinned: true,
				sensitiveMasked: Boolean(safe.sensitiveMasked),
				totalAmount: patch.totalAmount,
				transactedAt: patch.transactedAt,
				updatedAt: new Date().toISOString(),
				syncState: "local",
			});
			const afterReceipts = currentReceipts.map((receipt) =>
				receipt.receiptId === id ? changedReceipt : receipt,
			);
			return {
				afterReceipts,
				afterHousehold: reconcileReceiptItems(
					reverseReceiptItems(currentHousehold, original),
					changedReceipt,
				),
				changedReceipt,
			};
		}).then((saved) => {
			if (!saved) return false;
			const changed = receiptsRef.current.find(
				(receipt) => receipt.receiptId === id,
			);
			if (rememberRule && changed && !changed.sensitiveMasked) {
				writeStoreRules(
					setStoreRule(readStoreRules(), changed.storeKey, changed.categoryId),
				);
				analytics.track("store_rule_saved", { action: "set" });
			}
			analytics.track("record_edited", { fields: "core", count: 1 });
			return true;
		});
	/** #25: change category or move to trash for up to 200 records at once. */
	const bulkUpdate = (
		ids: string[],
		action: { kind: "category"; categoryId: string } | { kind: "trash" },
	): Promise<{ ok: number; failed: number }> =>
		receiptHouseholdQueue.current.enqueue(async () => {
			const beforeReceipts = receiptsRef.current;
			const beforeHousehold = householdRef.current;
			const now = new Date().toISOString();
			const targets = beforeReceipts.filter(
				(receipt) =>
					ids.includes(receipt.receiptId) &&
					!receipt.deletedAt &&
					!(action.kind === "category" && receipt.sensitiveMasked),
			);
			const skipped = ids.length - targets.length;
			if (!targets.length) return { ok: 0, failed: skipped };
			let afterHousehold = beforeHousehold;
			const changed = targets.map((receipt) => {
				if (action.kind === "trash") {
					afterHousehold = reverseReceiptItems(afterHousehold, receipt);
					return {
						...receipt,
						deletedAt: now,
						voidReason: null,
						updatedAt: now,
						syncState: "local" as const,
					};
				}
				const next = normalizeReceiptForStorage({
					...receipt,
					categoryId: action.categoryId,
					categoryPinned: true,
					updatedAt: now,
					syncState: "local",
				});
				afterHousehold = reconcileReceiptItems(
					reverseReceiptItems(afterHousehold, receipt),
					next,
				);
				return next;
			});
			const afterReceipts = beforeReceipts.map(
				(receipt) =>
					changed.find((item) => item.receiptId === receipt.receiptId) ??
					receipt,
			);
			try {
				await persistReceiptAndHouseholdChange({
					storage: window.localStorage,
					receiptStore,
					beforeReceipts,
					afterReceipts,
					beforeHousehold,
					afterHousehold,
					changedReceipts: changed,
				});
			} catch {
				return { ok: 0, failed: ids.length };
			}
			receiptsRef.current = afterReceipts;
			householdRef.current = afterHousehold;
			setReceipts(afterReceipts);
			setHousehold(afterHousehold);
			analytics.track("record_edited", {
				fields: action.kind === "trash" ? "trash" : "category",
				count: changed.length,
			});
			return { ok: changed.length, failed: skipped };
		});
	const removeTagFromReceipts = (tagId: string): Promise<boolean> =>
		receiptHouseholdQueue.current.enqueue(async () => {
			const beforeReceipts = receiptsRef.current;
			const now = new Date().toISOString();
			const changed = beforeReceipts
				.filter((receipt) => receipt.tagIds?.includes(tagId))
				.map((receipt) => ({
					...receipt,
					tagIds: (receipt.tagIds ?? []).filter((id) => id !== tagId),
					updatedAt: now,
				}));
			if (!changed.length) return true;
			const afterReceipts = beforeReceipts.map(
				(receipt) =>
					changed.find((item) => item.receiptId === receipt.receiptId) ??
					receipt,
			);
			try {
				await persistReceiptAndHouseholdChange({
					storage: window.localStorage,
					receiptStore,
					beforeReceipts,
					afterReceipts,
					beforeHousehold: householdRef.current,
					afterHousehold: householdRef.current,
					changedReceipts: changed,
				});
			} catch {
				return false;
			}
			receiptsRef.current = afterReceipts;
			setReceipts(afterReceipts);
			return true;
		});
	const updateHousehold = (
		next: HouseholdConsumptionState,
	): Promise<boolean> => {
		const before = householdRef.current;
		return receiptHouseholdQueue.current
			.enqueue(async () => {
				const merged = mergeHouseholdEdit(before, householdRef.current, next);
				writeHousehold(merged);
				householdRef.current = merged;
				setHousehold(merged);
				return true;
			})
			.catch(() => false);
	};
	const replaceReceipts = (backup: {
		receipts: Receipt[];
		household: HouseholdConsumptionState;
		dayCloses?: DayCloseRecord[];
	}): Promise<boolean> =>
		receiptHouseholdQueue.current.enqueue(async () => {
			if (!isLocalSnapshot(backup.receipts, backup.household)) return false;
			const normalized = backup.receipts.map(normalizeReceiptForStorage);
			const nextHousehold = normalizeShoppingEntryIds(backup.household);
			let nextCloses: DayCloseRecord[];
			try {
				nextCloses = await restoreLocalBackup({
					storage: window.localStorage,
					receiptStore,
					beforeReceipts: receiptsRef.current,
					beforeHousehold: householdRef.current,
					backup: { ...backup, receipts: normalized, household: nextHousehold },
				});
			} catch (error) {
				if (
					error instanceof LocalBackupRollbackError ||
					(error instanceof ReceiptHouseholdPersistError &&
						error.rollbackFailed)
				)
					setInitialization("failed");
				const closeLoad = await loadDayCloses(receiptStore);
				setDayCloseRecovery(closeLoad.status === "recovery" ? closeLoad : null);
				return false;
			}
			receiptsRef.current = normalized;
			householdRef.current = nextHousehold;
			setReceipts(normalized);
			setHousehold(nextHousehold);
			dayClosesRef.current = nextCloses;
			setDayCloses(nextCloses);
			setDayCloseRecovery(null);
			return true;
		});
	const clear = async () => {
		let serverDeleted = !apiBaseUrl;
		if (apiBaseUrl) {
			try {
				const response = await fetch(
					`${apiBaseUrl.replace(/\/$/, "")}/api/v1/device`,
					{
						method: "DELETE",
						headers: await installHeaders(channel, identity),
					},
				);
				serverDeleted = response.ok;
			} catch {
				serverDeleted = false;
			}
		}
		setReceipts([]);
		setHousehold(emptyHouseholdConsumption());
		await receiptStore.clearAll();
		dayClosesRef.current = [];
		setDayCloses([]);
		for (const key of Object.keys(window.localStorage))
			if (key.startsWith("sobidogam:")) window.localStorage.removeItem(key);
		setDayCloseRecovery(null);
		return serverDeleted;
	};
	const screen = (() => {
		if (path === "/" || path === "")
			return (
				<Landing
					channel={channel}
					onRecord={() => void recordFromClipboard()}
				/>
			);
		if (path === "/dogam") return <CatalogIndex channel={channel} />;
		if (path.startsWith("/dogam/")) {
			const category = categoryBySlug(path.split("/")[2] ?? "");
			return category ? (
				<CategoryDetail
					category={category}
					channel={channel}
					onCta={() =>
						analytics.track("category_page_cta", { group: category.groupId })
					}
				/>
			) : (
				<PublicNotFound channel={channel} />
			);
		}
		if (path === "/pricing") return <PricingPage channel={channel} />;
		if (path.startsWith("/guide/")) {
			const guide = guideBySlug(path.split("/")[2]);
			return guide ? (
				<GuidePage guide={guide} channel={channel} />
			) : (
				<PublicNotFound channel={channel} />
			);
		}
		if (path === "/privacy")
			return <LegalPage kind="privacy" channel={channel} />;
		if (path === "/terms") return <LegalPage kind="terms" channel={channel} />;
		if (path === "/support")
			return (
				<LegalPage
					kind="support"
					channel={channel}
					onHelpSearch={(resultCount) =>
						analytics.track("help_search", { resultCount })
					}
					onIssueOpen={() =>
						analytics.track("issue_report_open", { from: "support" })
					}
				/>
			);
		if (path === "/release-notes")
			return (
				<>
					<Header channel={channel} />
					<main className="section-wrap legal-page">
						<p className="eyebrow">업데이트 소식</p>
						<h1>
							기록은 조금씩 <em>더 단단해져요.</em>
						</h1>
						<div className="release-note">
							<span>2026.10.17</span>
							<h2>가계부로 매일 쓰는 기본기를 채웠어요</h2>
							<p>
								저장한 기록을 고치고, 여러 건을 한 번에 정리하고, 월급날 기준
								기간과 달력·분류 그래프로 볼 수 있어요. 카드 명세 합계와 맞춰
								보면 빠진 날짜를 알려 드려요. 바꾼 매장 분류는 다음부터
								기억해요.
							</p>
							<p>
								첫 화면 버튼이 바로 결제 알림 붙여 넣기로 이어지고, 안드로이드
								문자 공유와 아이폰 단축어로 앱 밖에서 바로 기록할 수 있어요. 집
								안 물건은 떨어질 날을 미리 알려 주고, 장보기 목록을 가족에게
								보낼 수 있어요. 정기 결제일은 휴대폰 달력에 넣을 수 있어요.
							</p>
							<p>
								개인정보 처리방침과 이용약관을 새로 정리했어요. 사진 자동 읽기가
								꺼져 있는 동안 사진은 서버로 가지 않고, 서버 백업은 쓰지 않고
								파일 백업만 써요.
							</p>
						</div>
						<div className="release-note">
							<span>2026.08.21</span>
							<h2>집에서 쓰는 것까지 한 흐름으로</h2>
							<p>
								식비·생활용품·반복비 예산과 집 안 소모품, 다 썼어, 장보기, 월말
								결산을 연결했습니다. 계좌·자산·대출은 가져오지 않습니다.
							</p>
						</div>
						<div className="release-note">
							<span>2026.08.07</span>
							<h2>소비가 기억으로 남도록</h2>
							<p>
								첫 표본에 한 줄·기분·상황을 더하고, 주간 이야기와 기억
								타임라인에서 다시 만날 수 있게 했습니다. 민감 업종에는 이 정보를
								저장하지 않으며, 유료 기능은 실제 결제가 검증된 채널에서만
								나타납니다.
							</p>
						</div>
						<div className="release-note">
							<span>2026.08.07</span>
							<h2>영수증 없이도 첫 표본</h2>
							<p>
								결제 알림 붙여넣기, 스크린샷·전자영수증 다중 선택,
								위플·편한가계부 기록 옮기기를 추가했습니다. 원문 알림과 가져온
								파일은 서버로 보내지 않습니다.
							</p>
						</div>
						<div className="release-note">
							<span>2026.08.06</span>
							<h2>첫 표본의 시작</h2>
							<p>
								128칸 카탈로그, 사진·직접 입력 기록, 도감·리포트·기록 백업을 한
								흐름으로 열었습니다.
							</p>
						</div>
					</main>
					<SiteFooter />
				</>
			);
		if (initialization !== "ready")
			return (
				<main className="app-main" aria-busy={initialization === "pending"}>
					<h1>
						{initialization === "pending"
							? "저장한 기록을 여는 중이에요"
							: "저장한 기록을 열지 못했어요"}
					</h1>
					{initialization === "failed" && (
						<>
							<p role="alert">
								기존 기록의 상태를 확인하지 못했어요. 브라우저 저장 공간을
								지우지 말고 다시 열어 주세요.
							</p>
							<button
								className="button"
								type="button"
								onClick={() => window.location.reload()}
							>
								다시 열기
							</button>
							<IssueReportButton channel={channel} screen="기록 열기 실패" />
						</>
					)}
				</main>
			);
		if (dayCloseRecovery && dayCloseRecoveryBlocksPath(path))
			return (
				<>
					<AppHeader channel={channel} title="기록 복구" />
					<main className="app-main">
						<DayCloseRecoveryNotice
							recovery={dayCloseRecovery}
							onDownload={(raw) =>
								downloadLocalRaw(raw, "sobidogam-day-closes-original.txt")
							}
							onNavigate={navigate}
						/>
					</main>
					<AppNav active="" />
				</>
			);
		if (path === "/app/onboarding") return <Onboarding channel={channel} />;
		if (path === "/app/entry")
			return (
				<>
					<AppHeader channel={channel} title="기록하는 길" />
					<main className="app-main">
						<RecordingRoutes channel={channel} />
					</main>
					<AppNav active="home" />
				</>
			);
		if (path === "/app/capture")
			return (
				<Capture
					entryNotice={recordError}
					onDrafts={saveDrafts}
					receipts={receipts}
					household={household}
					onManualSave={(draft) => persist([draft])}
					channel={channel}
					apiBaseUrl={apiBaseUrl}
					identity={identity}
					bridge={bridge}
				/>
			);
		if (path === "/app/confirm")
			return drafts.length ? (
				<Confirm
					household={household}
					drafts={drafts}
					onSave={persist}
					channel={channel}
					notice={draftNotice}
					receipts={receipts}
				/>
			) : (
				<AppHome
					closes={dayCloses}
					onCloseDay={(date, quick) => void openDayClose(date, quick)}
					onRecord={() => void recordFromClipboard()}
					onAllowance={() => setAllowanceOpen(true)}
					recordBusy={recordBusy}
					recordError={recordError}
					channel={channel}
					receipts={receipts}
					household={household}
					unlock={unlock}
					onUnlockClose={() => setUnlock(null)}
					onHouseholdChange={updateHousehold}
					bridge={bridge}
				/>
			);
		if (path === "/app/dogam")
			return (
				<AppDogam
					receipts={receipts}
					channel={channel}
					household={household}
					closes={dayCloses}
					onCloseDay={(date, quick) => void openDayClose(date, quick)}
					bridge={bridge}
				/>
			);
		if (path === "/app/household")
			return (
				<>
					<HouseholdPage
						channel={channel}
						receipts={receipts}
						state={household}
						onChange={updateHousehold}
						onRecordRecurring={(draft) => persist([draft])}
						onNavigate={navigate}
						onEvent={(name, props) => analytics.track(name, props)}
						onAddToCalendar={
							channel === "ait"
								? undefined
								: (payments) => {
										downloadBlob(
											icsBlob(
												payments.map((payment) => recurringReminder(payment)),
											),
											payments.length === 1
												? "sobidogam-payment-day.ics"
												: "sobidogam-payment-days.ics",
										);
										analytics.track("ics_downloaded", { kind: "recurring" });
									}
						}
						onShareList={(text) =>
							shareText({
								text,
								webUrl:
									"https://sobidogam.com/?utm_source=share&utm_medium=shopping",
								tossPath: "/app/household",
								channel,
								bridge,
							})
						}
					/>
					<AppNav active="household" />
				</>
			);
		if (path === "/app/records")
			return (
				<Records
					receipts={receipts}
					household={household}
					onDelete={deleteReceipt}
					onRefund={refundReceipt}
					onRestore={restoreReceipt}
					onUpdate={updateReceipt}
					onEdit={editReceipt}
					onBulk={bulkUpdate}
					channel={channel}
				/>
			);
		if (path === "/app/report")
			return (
				<Report
					household={household}
					closes={dayCloses}
					receipts={receipts}
					channel={channel}
					commerce={commerce}
					entitlements={entitlements}
					onEntitlements={setEntitlements}
					bridge={bridge}
				/>
			);
		if (path === "/app/plus")
			return (
				<>
					<AppHeader channel={channel} title="소비도감 플러스" />
					<PlusScreen
						channel={channel}
						commerce={commerce}
						entitlements={entitlements}
						onEntitlements={setEntitlements}
					/>
					<AppNav active="settings" />
				</>
			);
		if (path === "/app/help")
			return (
				<>
					<AppHeader channel={channel} title="도움말" />
					<main className="app-main">
						<HelpSearch
							channel={channel}
							initialId={window.location.hash.slice(1) || undefined}
							onSearch={(resultCount) =>
								analytics.track("help_search", { resultCount })
							}
							onIssueOpen={() =>
								analytics.track("issue_report_open", { from: "help" })
							}
						/>
					</main>
					<AppNav active="settings" />
				</>
			);
		if (path === "/app/settings")
			return (
				<Settings
					dayCloses={dayCloses}
					dayCloseRecovery={dayCloseRecovery ?? undefined}
					receipts={receipts}
					household={household}
					onClear={clear}
					onReplace={replaceReceipts}
					channel={channel}
					apiBaseUrl={apiBaseUrl}
					identity={identity}
					webPushPublicKey={webPushPublicKey}
					requestPushPermission={requestPushPermission}
					onRemoveTag={removeTagFromReceipts}
				/>
			);
		return (
			<AppHome
				closes={dayCloses}
				onCloseDay={(date, quick) => void openDayClose(date, quick)}
				onRecord={() => void recordFromClipboard()}
				onAllowance={() => setAllowanceOpen(true)}
				recordBusy={recordBusy}
				recordError={recordError}
				channel={channel}
				receipts={receipts}
				household={household}
				unlock={unlock}
				onUnlockClose={() => setUnlock(null)}
				onHouseholdChange={updateHousehold}
				bridge={bridge}
			/>
		);
	})();
	return (
		<>
			{screen}
			{dayCloseRecovery &&
			path.startsWith("/app") &&
			!dayCloseRecoveryBlocksPath(path) ? (
				<div className="app-main">
					<DayCloseRecoveryNotice
						recovery={dayCloseRecovery}
						onDownload={(raw) =>
							downloadLocalRaw(raw, "sobidogam-day-closes-original.txt")
						}
						onNavigate={navigate}
					/>
				</div>
			) : null}
			{closingDate && !dayCloseRecovery ? (
				<DayClose
					initialSaved={closeResult === "saved"}
					initialError={
						closeResult === "error"
							? "저장하지 못했어요. 여기서 다시 도장을 찍을 수 있어요."
							: ""
					}
					date={closingDate}
					receipts={receipts}
					household={household}
					closes={dayCloses}
					onSave={saveClose}
					onClose={() => setClosingDate(null)}
				/>
			) : null}
			{allowanceOpen && !unlock && !saveToast && !closingDate ? (
				<AllowanceEditor
					household={household}
					onChange={updateHousehold}
					onClose={() => setAllowanceOpen(false)}
					onSet={(source) => analytics.track("allowance_set", { source })}
				/>
			) : null}
			{saveToast && path.startsWith("/app") ? (
				<SaveToast
					key={saveToast.key}
					toast={saveToast}
					onUndo={undoSave}
					onDone={() => setSaveToast(null)}
				/>
			) : null}
			{installOffer &&
			!unlock &&
			!allowanceOpen &&
			!closingDate &&
			!saveToast &&
			path.startsWith("/app") ? (
				<InstallGuide
					onClose={() => setInstallOffer(false)}
					onOutcome={(outcome, platform) =>
						analytics.track("install_prompt", { outcome, platform })
					}
				/>
			) : null}
		</>
	);
}

export function ProductApp(props: ProductUiProps) {
	const [localData, setLocalData] = useState<LocalDataLoadResult>(() =>
		loadLocalData(window.localStorage),
	);
	const reload = () => setLocalData(loadLocalData(window.localStorage));
	if (localData.status === "recovery") {
		return (
			<LocalRecoveryScreen
				result={localData}
				onRetry={reload}
				onRecovered={reload}
			/>
		);
	}
	return <ReadyProductApp {...props} initialLocalData={localData} />;
}
